import React, { useEffect, useRef, useState } from "react";
import { Analytics } from "@vercel/analytics/react";
import SectionVisuals from "./components/SectionVisuals";
import TutorDashboard from "./TutorDashboard";
import ExecutiveDashboard from "./ExecutiveDashboard";
import MatchResults from "./MatchResults";

// Animated gradient background for hero
const AnimatedGradient = () => (
  <svg
    className="animated-gradient-bg"
    width="100%"
    height="100%"
    viewBox="0 0 1440 700"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient id="g1" cx="0.5" cy="0.5" r="0.7">
        <stop offset="0%" stopColor="#4f8cff" stopOpacity="0.18" />
        <stop offset="100%" stopColor="#a259ff" stopOpacity="0.06" />
      </radialGradient>
      <radialGradient id="g2" cx="0.7" cy="0.3" r="0.6">
        <stop offset="0%" stopColor="#a259ff" stopOpacity="0.12" />
        <stop offset="100%" stopColor="#4f8cff" stopOpacity="0.03" />
      </radialGradient>
    </defs>
    <ellipse className="g1" cx="600" cy="350" rx="600" ry="300" fill="url(#g1)" />
    <ellipse className="g2" cx="1100" cy="200" rx="400" ry="200" fill="url(#g2)" />
  </svg>
);

// Futuristic animated SVG lines
const FuturisticLines = () => (
  <svg
    className="futuristic-lines"
    width="100%"
    height="200"
    viewBox="0 0 1440 200"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <path d="M0 100 Q720 200 1440 100" stroke="#a259ff" strokeWidth="2" opacity="0.13" />
    <path d="M0 150 Q720 50 1440 150" stroke="#4f8cff" strokeWidth="2" opacity="0.09" />
  </svg>
);

// Remove all emojis from features
const features = [
  {
    title: "Machine Learning",
    desc: "Advanced algorithms that learn from every interaction to improve matching accuracy.",
  },
  {
    title: "Smart Heuristics",
    desc: "Data-driven insights that go beyond subject expertise to find perfect compatibility.",
  },
  {
    title: "Design-First",
    desc: "Beautiful, intuitive experiences that make learning feel effortless and engaging.",
  },
  {
    title: "Real Outcomes",
    desc: "Measurable results that prove learning effectiveness, not just session completion.",
  },
];

// Typewriter effect for 'Built for Everyone' section
function TypewriterWord({ words, typingSpeed = 90, deletingSpeed = 60, pause = 1200 }) {
  const [index, setIndex] = useState(0); // which word
  const [displayed, setDisplayed] = useState(words[0]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [charIdx, setCharIdx] = useState(words[0].length);

  useEffect(() => {
    let timeout;
    const currentWord = words[index];
    if (!isDeleting && charIdx < currentWord.length) {
      timeout = setTimeout(() => {
        setDisplayed(currentWord.slice(0, charIdx + 1));
        setCharIdx(charIdx + 1);
      }, typingSpeed);
    } else if (isDeleting && charIdx > 0) {
      timeout = setTimeout(() => {
        setDisplayed(currentWord.slice(0, charIdx - 1));
        setCharIdx(charIdx - 1);
      }, deletingSpeed);
    } else if (!isDeleting && charIdx === currentWord.length) {
      timeout = setTimeout(() => setIsDeleting(true), pause);
    } else if (isDeleting && charIdx === 0) {
      timeout = setTimeout(() => {
        setIsDeleting(false);
        setIndex((index + 1) % words.length);
      }, 400);
    }
    return () => clearTimeout(timeout);
  }, [charIdx, isDeleting, index, words, typingSpeed, deletingSpeed, pause]);

  return (
    <span>
      {displayed}
      <span className="typewriter-cursor">|</span>
    </span>
  );
}

// Custom hook for animating on visible
function useAnimateOnVisible(showTutorPlatform, showStudentPlatform) {
  const ref = useRef();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Remove .visible when leaving homepage
    if (showTutorPlatform || showStudentPlatform) {
      el.classList.remove("visible");
      return;
    }
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("visible");
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [showTutorPlatform, showStudentPlatform]);
  return ref;
}

// Custom hook for animating a list of elements on visible with stagger
function useStaggeredAnimateOnVisible(count, baseClass, showTutorPlatform, showStudentPlatform) {
  const refs = useRef([]);
  useEffect(() => {
    const nodes = refs.current;
    if (!nodes.length) return;
    // Remove .visible when leaving homepage
    if (showTutorPlatform || showStudentPlatform) {
      nodes.forEach((node) => node && node.classList.remove("visible"));
      return;
    }
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.index);
            setTimeout(() => {
              entry.target.classList.add("visible");
            }, idx * 120); // 120ms stagger
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    nodes.forEach((node) => node && observer.observe(node));
    return () => observer.disconnect();
  }, [count, showTutorPlatform, showStudentPlatform]);
  return refs;
}

// Student Platform Component
const StudentPlatform = ({
  currentStep,
  formData,
  handleFormUpdate,
  nextStep,
  prevStep,
  setShowStudentPlatform,
  setCurrentStep,
}) => {
  const [matchResults, setMatchResults] = useState(null);

  const handleMultiSelect = (field, value) => {
    const current = formData[field] || [];
    const updated = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    handleFormUpdate(field, updated);
  };

  const [otherLanguage, setOtherLanguage] = useState("");
  const [showOtherLanguageInput, setShowOtherLanguageInput] = useState(false);

  const handleOtherLanguage = () => {
    const standardLanguages = ["English", "Spanish", "Mandarin", "Hindi", "French"];
    const filteredLanguages = (formData.languagePreference || []).filter((lang) => standardLanguages.includes(lang));

    if (showOtherLanguageInput) {
      // Remove other langauge input
      setShowOtherLanguageInput(false);
      setOtherLanguage("");
      handleFormUpdate("languagePreference", filteredLanguages);
    } else {
      setShowOtherLanguageInput(true);
    }
  };

  const handleOtherLanguageChange = (value) => {
    const trimmedValue = value.trim();
    const formattedValue =
      trimmedValue.length > 1
        ? trimmedValue.charAt(0).toUpperCase() + trimmedValue.slice(1).toLowerCase()
        : trimmedValue.toUpperCase();
    setOtherLanguage(formattedValue);
    const standardLanguages = ["English", "Spanish", "Mandarin", "Hindi", "French"];
    const filteredLanguages = (formData.languagePreference || []).filter((lang) => standardLanguages.includes(lang));

    handleFormUpdate("languagePreference", formattedValue ? [...filteredLanguages, formattedValue] : filteredLanguages);
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 0:
        return true;
      case 1:
        return formData.firstName.trim() !== "" && formData.lastName.trim() !== "" && formData.email.trim() !== "";
      case 2:
        return formData.timezone !== "";
      case 3:
        return formData.subjectNeeds && formData.subjectNeeds.length > 0;
      case 4:
        return formData.learningStyle && formData.learningStyle.length > 0;
      case 5:
        return formData.learningPace !== "";
      case 6:
        return formData.sessionFormat && formData.sessionFormat.length > 0;
      case 7:
        return formData.studentLevel !== "";
      case 8:
        return formData.learningApproach !== "";
      case 9:
        return formData.availabilityFlexibility !== "";
      case 10:
        return formData.languagePreference && formData.languagePreference.length > 0;
      case 11:
        return formData.helpFocus !== "";
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    const submissionData = {
      timestamp: new Date().toISOString(),
      studentProfile: {
        contact: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
        },
        preferences: {
          timezone: formData.timezone,
          subjectNeeds: formData.subjectNeeds,
          learningStyle: formData.learningStyle,
          learningPace: formData.learningPace,
          sessionFormat: formData.sessionFormat,
          studentLevel: formData.studentLevel,
          learningApproach: formData.learningApproach,
          availabilityFlexibility: formData.availabilityFlexibility,
          languagePreference: formData.languagePreference,
          helpFocus: formData.helpFocus,
        },
      },
      status: "submitted",
      completionRate: "100%",
    };

    console.log("=== ZENTORA STUDENT MATCHING REQUEST ===");
    console.log(JSON.stringify(submissionData, null, 2));
    console.log("=== END SUBMISSION ===");

    try {
      const response = await fetch("https://zentora-backend-dd6w.onrender.com/api/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setMatchResults(result.tutor_matches);
      console.log("✅ SUCCESS! Submitted to database and fetched match results:", result);
    } catch (err) {
      console.error("❌ Submission or matching failed:", err);
      alert("❌ Submission or matching failed: " + err.message);
    }

    nextStep();
  };

  const steps = [
    // Step 0: Welcome
    {
      component: (
        <div className="platform-welcome-step student-centered-step">
          <h1 className="platform-welcome-title student-title">
            Find Your Perfect
            <br />
            <span className="platform-gradient-text student-title-gradient">Tutor Match</span>
          </h1>
          <p className="platform-welcome-subtitle student-subtitle">
            Let's understand your learning preferences to connect you with the ideal tutor who matches your style and
            goals.
          </p>
          <button className="platform-welcome-cta student-cta" onClick={nextStep}>
            Start Matching
            <span className="platform-btn-arrow">→</span>
          </button>
        </div>
      ),
    },
    // Step 1: Name & Email
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">1 →</span>
            <h2 className="platform-step-title">Your Details</h2>
          </div>
          <p className="platform-step-instruction">Please enter your name and email address.</p>
          <div style={{ width: "100%", maxWidth: 400, display: "flex", flexDirection: "column", gap: 18 }}>
            <input
              className="platform-select"
              type="text"
              placeholder="First Name"
              value={formData.firstName}
              onChange={(e) => handleFormUpdate("firstName", e.target.value.trim())}
              style={{ marginBottom: 12 }}
            />
            <input
              className="platform-select"
              type="text"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={(e) => handleFormUpdate("lastName", e.target.value.trim())}
              style={{ marginBottom: 12 }}
            />
            <input
              className="platform-select"
              type="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={(e) => handleFormUpdate("email", e.target.value.trim())}
            />
          </div>
        </div>
      ),
    },
    // Step 2: Timezone
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">2 →</span>
            <h2 className="platform-step-title">Your Timezone</h2>
          </div>
          <p className="platform-step-instruction">Select your current timezone</p>
          <select
            className="platform-select"
            style={{ maxWidth: 400 }}
            value={formData.timezone || ""}
            onChange={(e) => handleFormUpdate("timezone", e.target.value)}>
            <option value="">Select timezone...</option>
            <option value="PT">Pacific Time (PT)</option>
            <option value="MT">Mountain Time (MT)</option>
            <option value="CT">Central Time (CT)</option>
            <option value="ET">Eastern Time (ET)</option>
            <option value="GMT">GMT</option>
            <option value="CET">Central European Time (CET)</option>
            <option value="IST">India Standard Time (IST)</option>
            <option value="JST">Japan Standard Time (JST)</option>
            <option value="AEST">Australian Eastern Time (AEST)</option>
          </select>
        </div>
      ),
    },
    // Step 3: Subject Needs (Student Platform)
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">3 →</span>
            <h2 className="platform-step-title">Subject Needs</h2>
          </div>
          <p className="platform-step-instruction student-chat-instruction">
            Which subject(s) do you need help with? Select all that apply.
          </p>
          <div className="platform-options-grid student-subjects-grid">
            {[
              {
                subject: "Mathematics",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28">
                    <text x="14" y="19" textAnchor="middle" fontSize="20" fontFamily="serif" fill="#23273a">
                      ∑
                    </text>
                  </svg>
                ),
              },
              {
                subject: "Physics",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28">
                    <circle cx="14" cy="14" r="9" stroke="#23273a" strokeWidth="2" fill="none" />
                    <circle cx="14" cy="8" r="2" fill="#a259ff" />
                    <circle cx="20" cy="16" r="1.5" fill="#4f8cff" />
                    <circle cx="9" cy="18" r="1.2" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                subject: "Chemistry",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28">
                    <rect x="9" y="7" width="10" height="12" rx="3" stroke="#23273a" strokeWidth="2" fill="none" />
                    <circle cx="14" cy="19" r="2" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                subject: "Biology",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28">
                    <ellipse cx="14" cy="14" rx="8" ry="4" stroke="#23273a" strokeWidth="2" fill="none" />
                    <path d="M10 10 Q14 18 18 10" stroke="#a259ff" strokeWidth="1.5" fill="none" />
                  </svg>
                ),
              },
              {
                subject: "Computer Science",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28">
                    <text x="14" y="19" textAnchor="middle" fontSize="18" fontFamily="monospace" fill="#23273a">
                      {"{}"}
                    </text>
                  </svg>
                ),
              },
              {
                subject: "English",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28">
                    <rect x="7" y="8" width="14" height="12" rx="2" stroke="#23273a" strokeWidth="2" fill="none" />
                    <line x1="10" y1="12" x2="18" y2="12" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
              {
                subject: "History",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28">
                    <rect x="9" y="8" width="10" height="12" rx="2" stroke="#23273a" strokeWidth="2" fill="none" />
                    <line x1="14" y1="8" x2="14" y2="20" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
              {
                subject: "Economics",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28">
                    <polyline points="7,19 14,9 21,19" stroke="#23273a" strokeWidth="2" fill="none" />
                    <circle cx="14" cy="9" r="1.5" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                subject: "Psychology",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28">
                    <text x="14" y="19" textAnchor="middle" fontSize="18" fontFamily="serif" fill="#23273a">
                      Ψ
                    </text>
                  </svg>
                ),
              },
              {
                subject: "Engineering",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28">
                    <circle cx="14" cy="14" r="8" stroke="#23273a" strokeWidth="2" fill="none" />
                    <circle cx="14" cy="14" r="3" fill="#a259ff" />
                    <rect x="13" y="6" width="2" height="4" fill="#4f8cff" />
                    <rect x="13" y="18" width="2" height="4" fill="#4f8cff" />
                  </svg>
                ),
              },
            ].map(({ subject, icon }) => (
              <button
                key={subject}
                className={`platform-option-btn student-subject-btn ${
                  (formData.subjectNeeds || []).includes(subject) ? "selected" : ""
                }`}
                onClick={() => handleMultiSelect("subjectNeeds", subject)}>
                <span className="platform-option-letter platform-option-icon">{icon}</span>
                {subject}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    // Step 4: Learning Style
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">4 →</span>
            <h2 className="platform-step-title">Learning Style</h2>
          </div>
          <p className="platform-step-instruction">How do you learn best? Select all that apply.</p>
          <div className="platform-options-grid">
            {[
              {
                value: "Visual",
                label: "Visual learning",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <ellipse cx="14" cy="14" rx="10" ry="6" stroke="#23273a" strokeWidth="2" />
                    <circle cx="14" cy="14" r="2.5" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Interactive",
                label: "Auditory learning/Interactive discussions",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <path d="M8 14c0-3.3 2.7-6 6-6s6 2.7 6 6-2.7 6-6 6" stroke="#23273a" strokeWidth="2" />
                    <path d="M14 18v-4" stroke="#a259ff" strokeWidth="2" strokeLinecap="round" />
                    <circle cx="14" cy="14" r="1.5" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Hands-on",
                label: "Hands-on practice",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="8" y="10" width="12" height="8" rx="2" stroke="#23273a" strokeWidth="2" />
                    <path d="M10 18v2h8v-2" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
              {
                value: "Conceptual",
                label: "Conceptual understanding focused",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="8" stroke="#23273a" strokeWidth="2" />
                    <path d="M14 6v16M6 14h16" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
              {
                value: "Problem-solving",
                label: "Problem-solving focused",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="7" y="7" width="14" height="14" rx="4" stroke="#23273a" strokeWidth="2" />
                    <path d="M11 17l6-6M11 11h6v6" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
            ].map((style, index) => (
              <button
                key={style.value}
                className={`platform-option-btn ${
                  (formData.learningStyle || []).includes(style.value) ? "selected" : ""
                }`}
                onClick={() => handleMultiSelect("learningStyle", style.value)}>
                <span className="platform-option-letter platform-option-icon">{style.icon}</span>
                {style.label}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    // Step 5: Learning Pace
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">5 →</span>
            <h2 className="platform-step-title">Preferred Learning Pace</h2>
          </div>
          <p className="platform-step-instruction">What pace works best for you?</p>
          <div className="platform-options-grid">
            {[
              {
                value: "Slow",
                label: "Slow and detailed",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="10" stroke="#23273a" strokeWidth="2" />
                    <path d="M14 8v6l-4 4" stroke="#a259ff" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                ),
              },
              {
                value: "Moderate",
                label: "Moderate pace",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="10" stroke="#23273a" strokeWidth="2" />
                    <path d="M14 8v6l2.5 2.5" stroke="#a259ff" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                ),
              },
              {
                value: "Fast-paced",
                label: "Fast-paced",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="10" stroke="#23273a" strokeWidth="2" />
                    <path d="M14 8v6l4 4" stroke="#a259ff" strokeWidth="2" strokeLinecap="round" />
                    <path d="M18 18l2 2" stroke="#a259ff" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                ),
              },
            ].map((pace, index) => (
              <button
                key={pace.value}
                className={`platform-option-btn ${formData.learningPace === pace.value ? "selected" : ""}`}
                onClick={() => handleFormUpdate("learningPace", pace.value)}>
                <span className="platform-option-letter platform-option-icon">{pace.icon}</span>
                {pace.label}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    // Step 6: Session Format
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">6 →</span>
            <h2 className="platform-step-title">Session Format Preference</h2>
          </div>
          <p className="platform-step-instruction">Which format(s) are you comfortable with? Select all that apply.</p>
          <div className="platform-options-grid">
            {[
              {
                value: "Text-based",
                label: "Text-based (chat)",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="5" y="8" width="18" height="10" rx="4" stroke="#23273a" strokeWidth="2" />
                    <path d="M9 18l2-2h6l2 2" stroke="#a259ff" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                ),
              },
              {
                value: "Video call",
                label: "Video call",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="6" y="9" width="12" height="10" rx="3" stroke="#23273a" strokeWidth="2" />
                    <polygon points="20,12 24,14 20,16" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Screen sharing",
                label: "Screen sharing",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="5" y="8" width="18" height="10" rx="2" stroke="#23273a" strokeWidth="2" />
                    <rect x="11" y="14" width="6" height="2" rx="1" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Collaborative whiteboard",
                label: "Collaborative whiteboard",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="6" y="8" width="16" height="10" rx="2" stroke="#23273a" strokeWidth="2" />
                    <path d="M9 16l4-4 4 4" stroke="#a259ff" strokeWidth="1.5" strokeLinecap="round" />
                    <circle cx="14" cy="14" r="1.5" fill="#a259ff" />
                  </svg>
                ),
              },
            ].map((format, index) => (
              <button
                key={format.value}
                className={`platform-option-btn ${
                  (formData.sessionFormat || []).includes(format.value) ? "selected" : ""
                }`}
                onClick={() => handleMultiSelect("sessionFormat", format.value)}>
                <span className="platform-option-letter platform-option-icon">{format.icon}</span>
                {format.label}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    // Step 7: Student Level
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">7 →</span>
            <h2 className="platform-step-title">Your Academic Level</h2>
          </div>
          <p className="platform-step-instruction">What best describes your current level?</p>
          <div className="platform-options-grid">
            {[
              {
                value: "Beginner",
                label: "Need foundational help",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="8" y="12" width="12" height="8" rx="2" stroke="#23273a" strokeWidth="2" />
                    <rect x="12" y="8" width="4" height="4" rx="1.5" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Intermediate",
                label: "Understand basic concepts",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="9" stroke="#23273a" strokeWidth="2" />
                    <circle cx="14" cy="11" r="2" fill="#a259ff" />
                    <circle cx="14" cy="17" r="2" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Advanced",
                label: "Advanced learner",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="6" y="10" width="16" height="10" rx="3" stroke="#23273a" strokeWidth="2" />
                    <path d="M14 10v10" stroke="#a259ff" strokeWidth="1.5" />
                    <circle cx="14" cy="10" r="2" fill="#a259ff" />
                  </svg>
                ),
              },
            ].map((level, index) => (
              <button
                key={level.value}
                className={`platform-option-btn ${formData.studentLevel === level.value ? "selected" : ""}`}
                onClick={() => handleFormUpdate("studentLevel", level.value)}>
                <span className="platform-option-letter platform-option-icon">{level.icon}</span>
                {level.label}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    // Step 8: Learning Approach
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">8 →</span>
            <h2 className="platform-step-title">Learning Approach</h2>
          </div>
          <p className="platform-step-instruction">How do you prefer to learn?</p>
          <div className="platform-options-grid">
            {[
              {
                value: "Structured",
                label: "Structured lesson plans",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="7" y="8" width="14" height="12" rx="3" stroke="#23273a" strokeWidth="2" />
                    <path d="M10 12h8M10 16h8" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
              {
                value: "Flexible",
                label: "Flexible/adaptive approach",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="5" y="8" width="18" height="10" rx="4" stroke="#23273a" strokeWidth="2" />
                    <path d="M9 18l2-2h6l2 2" stroke="#a259ff" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                ),
              },
            ].map((approach, index) => (
              <button
                key={approach.value}
                className={`platform-option-btn ${formData.learningApproach === approach.value ? "selected" : ""}`}
                onClick={() => handleFormUpdate("learningApproach", approach.value)}>
                <span className="platform-option-letter platform-option-icon">{approach.icon}</span>
                {approach.label}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    // Step 9: Availability
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">9 →</span>
            <h2 className="platform-step-title">Your Availability</h2>
          </div>
          <p className="platform-step-instruction">How flexible is your schedule?</p>
          <div className="platform-options-grid">
            {[
              {
                value: "Fixed schedule",
                label: "Fixed schedule",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="6" y="8" width="16" height="12" rx="3" stroke="#23273a" strokeWidth="2" />
                    <rect x="10" y="12" width="8" height="4" rx="1" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Flexible timing",
                label: "Flexible timing",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="6" y="8" width="16" height="12" rx="3" stroke="#23273a" strokeWidth="2" />
                    <path d="M10 16c2-2 6 2 8 0" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
            ].map((availability, index) => (
              <button
                key={availability.value}
                className={`platform-option-btn ${
                  formData.availabilityFlexibility === availability.value ? "selected" : ""
                }`}
                onClick={() => handleFormUpdate("availabilityFlexibility", availability.value)}>
                <span className="platform-option-letter platform-option-icon">{availability.icon}</span>
                {availability.label}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    // Step 10: Language Preference
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">10 →</span>
            <h2 className="platform-step-title">Language Preference</h2>
          </div>
          <p className="platform-step-instruction">
            Which language(s) do you prefer for tutoring? Select all that apply.
          </p>
          <div className="platform-options-grid">
            {[
              {
                value: "English",
                label: "English",
                icon: (
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <rect x="4" y="7" width="28" height="22" rx="6" stroke="#23273a" strokeWidth="2.2" fill="#fff" />
                    <text
                      x="18"
                      y="25"
                      textAnchor="middle"
                      fontSize="16"
                      fontFamily="sans-serif"
                      fill="#a259ff"
                      fontWeight="bold">
                      EN
                    </text>
                  </svg>
                ),
              },
              {
                value: "Spanish",
                label: "Spanish",
                icon: (
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <rect x="4" y="7" width="28" height="22" rx="6" stroke="#23273a" strokeWidth="2.2" fill="#fff" />
                    <text
                      x="18"
                      y="25"
                      textAnchor="middle"
                      fontSize="16"
                      fontFamily="sans-serif"
                      fill="#a259ff"
                      fontWeight="bold">
                      ES
                    </text>
                  </svg>
                ),
              },
              {
                value: "Mandarin",
                label: "Mandarin",
                icon: (
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <rect x="4" y="7" width="28" height="22" rx="6" stroke="#23273a" strokeWidth="2.2" fill="#fff" />
                    <text
                      x="18"
                      y="25"
                      textAnchor="middle"
                      fontSize="16"
                      fontFamily="sans-serif"
                      fill="#a259ff"
                      fontWeight="bold">
                      中
                    </text>
                  </svg>
                ),
              },
              {
                value: "Hindi",
                label: "Hindi",
                icon: (
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <rect x="4" y="7" width="28" height="22" rx="6" stroke="#23273a" strokeWidth="2.2" fill="#fff" />
                    <text
                      x="18"
                      y="25"
                      textAnchor="middle"
                      fontSize="16"
                      fontFamily="sans-serif"
                      fill="#a259ff"
                      fontWeight="bold">
                      हि
                    </text>
                  </svg>
                ),
              },
              {
                value: "French",
                label: "French",
                icon: (
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <rect x="4" y="7" width="28" height="22" rx="6" stroke="#23273a" strokeWidth="2.2" fill="#fff" />
                    <text
                      x="18"
                      y="25"
                      textAnchor="middle"
                      fontSize="16"
                      fontFamily="sans-serif"
                      fill="#a259ff"
                      fontWeight="bold">
                      FR
                    </text>
                  </svg>
                ),
              },
              {
                value: "Other",
                label: "Other",
                icon: (
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <rect x="4" y="7" width="28" height="22" rx="6" stroke="#23273a" strokeWidth="2.2" fill="#fff" />
                    <circle cx="18" cy="18" r="6" fill="#a259ff" />
                  </svg>
                ),
              },
            ].map((lang, index) => (
              <button
                key={lang.value}
                className={`platform-option-btn ${
                  (formData.languagePreference || []).includes(lang.value) ||
                  (lang.value === "Other" && otherLanguage !== "")
                    ? "selected"
                    : ""
                }`}
                onClick={() =>
                  lang.value === "Other" ? handleOtherLanguage() : handleMultiSelect("languagePreference", lang.value)
                }>
                <span
                  className="platform-option-letter platform-option-icon"
                  style={{ padding: "0 6px 0 0", minWidth: 40 }}>
                  {lang.icon}
                </span>
                {lang.label}
              </button>
            ))}
          </div>
          {showOtherLanguageInput && (
            <div>
              <div
                style={{
                  width: "90%",
                  maxWidth: 400,
                  display: "flex",
                  flexDirection: "column",
                  gap: 18,
                  marginTop: "10px",
                }}>
                <input
                  className="platform-select"
                  type="text"
                  placeholder="Enter other language"
                  value={otherLanguage}
                  onChange={(e) => handleOtherLanguageChange(e.target.value)}
                  style={{ marginBottom: 12 }}
                />
              </div>
            </div>
          )}
        </div>
      ),
    },
    // Step 11: Learning Goal
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">11 →</span>
            <h2 className="platform-step-title">Learning Goal</h2>
          </div>
          <p className="platform-step-instruction">What's your primary learning goal?</p>
          <div className="platform-options-grid">
            {[
              {
                value: "Assignment/Homework",
                label: "Assignment/Homework help",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="8" y="8" width="12" height="12" rx="2" stroke="#23273a" strokeWidth="2" />
                    <path d="M12 12h4M12 16h4" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
              {
                value: "Conceptual",
                label: "Long-term conceptual understanding",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="8" stroke="#23273a" strokeWidth="2" />
                    <path d="M10 18c2-2 6 2 8 0" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
              {
                value: "Exam preparation",
                label: "Exam preparation",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="8" y="8" width="12" height="12" rx="2" stroke="#23273a" strokeWidth="2" />
                    <path d="M14 12v4" stroke="#a259ff" strokeWidth="1.5" />
                    <circle cx="14" cy="16" r="1.5" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Project",
                label: "Project help",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="8" y="8" width="12" height="12" rx="2" stroke="#23273a" strokeWidth="2" />
                    <path d="M12 16l4-4" stroke="#a259ff" strokeWidth="1.5" />
                    <circle cx="16" cy="12" r="1.5" fill="#a259ff" />
                  </svg>
                ),
              },
            ].map((focus, index) => (
              <button
                key={focus.value}
                className={`platform-option-btn ${formData.helpFocus === focus.value ? "selected" : ""}`}
                onClick={() => handleFormUpdate("helpFocus", focus.value)}>
                <span className="platform-option-letter platform-option-icon">{focus.icon}</span>
                {focus.label}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    // Step 12: Completion
    {
      component: (
        <div className="platform-completion-step">
          <div className="platform-success-icon">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h1 className="platform-step-title">Matching Complete!</h1>
          <p className="platform-step-subtitle">
            Thank you for sharing your preferences. We're now finding the perfect tutors who match your learning style
            and needs.
          </p>
        </div>
      ),
    },
  ];

  // FOR TESTING THE MATCH RESULTS PAGE ONLY
  // useEffect(() => {
  //   setCurrentStep(12);
  //   const matches = {
  //     "Computer Science": [
  //       {
  //         id: "12345",
  //         score: "99%",
  //         firstName: "Emma",
  //         lastName: "Anderson",
  //         email: "emmaa3@gmail.com",
  //         introVideo: "https://youtu.be/Y-x0efG1seA",
  //         background: ["UIUC Student"],
  //         matchJustification: [
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //         ],
  //       },
  //       {
  //         id: "12345",
  //         score: "99%",
  //         firstName: "Emma",
  //         lastName: "Anderson",
  //         email: "emmaa3@gmail.com",
  //         introVideo: "https://youtu.be/Y-x0efG1seA",
  //         background: ["UIUC Student"],
  //         matchJustification: [
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //         ],
  //       },
  //       {
  //         id: "12345",
  //         score: "99%",
  //         firstName: "Emma",
  //         lastName: "Anderson",
  //         email: "emmaa3@gmail.com",
  //         introVideo: "https://youtu.be/Y-x0efG1seA",
  //         background: ["UIUC Student"],
  //         matchJustification: [
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //         ],
  //       },
  //     ],
  //     Mathematics: [
  //       {
  //         id: "12345",
  //         score: "99%",
  //         firstName: "Emma",
  //         lastName: "Anderson",
  //         email: "emmaa3@gmail.com",
  //         introVideo: "https://youtu.be/Y-x0efG1seA",
  //         background: ["UIUC Student"],
  //         matchJustification: [
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //         ],
  //       },
  //       {
  //         id: "12345",
  //         score: "99%",
  //         firstName: "Emma",
  //         lastName: "Anderson",
  //         email: "emmaa3@gmail.com",
  //         introVideo: "https://youtu.be/Y-x0efG1seA",
  //         background: ["UIUC Student"],
  //         matchJustification: [
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //         ],
  //       },
  //       {
  //         id: "12345",
  //         score: "99%",
  //         firstName: "Emma",
  //         lastName: "Anderson",
  //         email: "emmaa3@gmail.com",
  //         introVideo: "https://youtu.be/Y-x0efG1seA",
  //         background: ["UIUC Student"],
  //         matchJustification: [
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //         ],
  //       },
  //     ],
  //     Biology: [
  //       {
  //         id: "12345",
  //         score: "99%",
  //         firstName: "Emma",
  //         lastName: "Anderson",
  //         email: "emmaa3@gmail.com",
  //         introVideo: "https://youtu.be/Y-x0efG1seA",
  //         background: ["UIUC Student"],
  //         matchJustification: [
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //         ],
  //       },
  //       {
  //         id: "12345",
  //         score: "99%",
  //         firstName: "Emma",
  //         lastName: "Anderson",
  //         email: "emmaa3@gmail.com",
  //         introVideo: "https://youtu.be/Y-x0efG1seA",
  //         background: ["UIUC Student"],
  //         matchJustification: [
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //         ],
  //       },
  //       {
  //         id: "12345",
  //         score: "99%",
  //         firstName: "Emma",
  //         lastName: "Anderson",
  //         email: "emmaa3@gmail.com",
  //         introVideo: "https://youtu.be/Y-x0efG1seA",
  //         background: ["UIUC Student"],
  //         matchJustification: [
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //           {
  //             heading: "Heading heading heading",
  //             description: "blah blah blah blah blah blah blah blah blah blah blah blah blah blah",
  //           },
  //         ],
  //       },
  //     ],
  //   };
  //   setMatchResults(matches);
  // }, []);

  const currentStepData = steps[currentStep];
  const progress = (currentStep / (steps.length - 1)) * 100;

  return currentStep < steps.length - 1 ? (
    <div className="zentora-platform student-platform-bg">
      {/* Floating Zentora Logo (centered, on top of all content) */}
      {/* <div className="zentora-floating-logo student-floating-logo">
        <img src="/zentora-transparent-white.png" alt="Zentora Logo" />
      </div> */}
      {/* Floating Progress Bar */}
      <div className="student-floating-progress-bar">
        <div className="student-progress-bar-outer">
          <div className="student-progress-bar-inner" style={{ width: `${progress}%` }}></div>
        </div>
        <div className="student-progress-bar-label">{Math.round(progress)}%</div>
      </div>
      {/* Beautiful Back Home Button */}
      <button
        className="student-back-home-btn"
        onClick={() => {
          setShowStudentPlatform(false);
          setCurrentStep(0);
        }}>
        <span className="student-back-home-icon">←</span> Back to Home
      </button>
      <div className="platform-form-container student-form-container">
        {/* Animated step transitions (slide/fade) */}
        <div
          className="platform-form-content student-form-content student-step-animate student-step-slide"
          key={currentStep}
          aria-live="polite">
          {currentStepData.component}
        </div>
        {currentStep > 0 && currentStep < steps.length - 1 && (
          <div className="platform-form-navigation student-form-navigation">
            {currentStep > 0 && (
              <button
                className="tutor-form-action-btn tutor-form-action-back"
                onClick={prevStep}
                aria-label="Previous Step"
                type="button">
                <span className="tutor-form-action-icon tutor-form-action-back-icon">←</span>
                Back
              </button>
            )}
            <button
              className={`tutor-form-action-btn tutor-form-action-next${isStepValid() ? "" : " disabled"}`}
              onClick={currentStep === steps.length - 2 ? handleSubmit : nextStep}
              aria-label={currentStep === steps.length - 2 ? "Submit" : "Next Step"}
              type="button"
              disabled={!isStepValid()}>
              {currentStep === steps.length - 2 ? "Submit" : "OK"}
              <span className="tutor-form-action-icon tutor-form-action-next-icon">→</span>
            </button>
          </div>
        )}
      </div>
      <style jsx>{`
        /* Student Platform Custom Styles */
        .student-platform-bg {
          background: linear-gradient(120deg, #f3f3f7 0%, #e9e6ff 100%);
          min-height: 100vh;
          font-family: "FK Grotesk", sans-serif;
        }
        /* Slide/Fade Step Animation */
        .student-step-slide {
          animation: studentStepSlideIn 0.7s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes studentStepSlideIn {
          from {
            opacity: 0;
            transform: translateY(48px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: none;
          }
        }
        .student-step-animate {
          animation: studentStepFadeIn 0.7s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes studentStepFadeIn {
          from {
            opacity: 0;
            transform: translateY(32px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: none;
          }
        }
        /* Floating Glowing Progress Bar */
        .student-floating-progress-bar {
          position: fixed;
          top: 32px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 1200;
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 420px;
          max-width: 90vw;
          pointer-events: none;
        }
        .student-progress-bar-outer {
          width: 100%;
          height: 14px;
          background: rgba(200, 200, 255, 0.13);
          border-radius: 12px;
          box-shadow: 0 0 32px 0 #a259ff44, 0 2px 12px #4f8cff22;
          overflow: hidden;
          margin-bottom: 6px;
          position: relative;
        }
        .student-progress-bar-inner {
          height: 100%;
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          border-radius: 12px;
          box-shadow: 0 0 18px 2px #a259ff88, 0 0 8px #4f8cff66;
          transition: width 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .student-progress-bar-label {
          color: #a259ff;
          font-family: "FK Grotesk", sans-serif;
          font-weight: 700;
          font-size: 1.1rem;
          text-shadow: 0 2px 8px #fff, 0 0 8px #a259ff33;
          margin-top: 0px;
          letter-spacing: 0.02em;
        }
        @media (max-width: 600px) {
          .student-floating-progress-bar {
            top: 16px;
            width: 98vw;
            max-width: 98vw;
          }
        }
        /* Beautiful Back Home Button */
        .student-back-home-btn {
          position: fixed;
          top: 32px;
          left: 32px;
          z-index: 1201;
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          border: none;
          border-radius: 32px;
          font-family: "FK Grotesk", sans-serif;
          font-weight: 600;
          font-size: 1.08rem;
          padding: 13px 32px 13px 22px;
          box-shadow: 0 4px 24px #a259ff33, 0 1.5px 6px #4f8cff22;
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
          outline: none;
        }
        .student-back-home-btn:hover,
        .student-back-home-btn:focus {
          background: linear-gradient(90deg, #a259ff 0%, #4f8cff 100%);
          box-shadow: 0 8px 32px #a259ff55, 0 2px 12px #4f8cff33;
          transform: translateY(-2px) scale(1.04);
        }
        .student-back-home-icon {
          font-size: 1.2em;
          margin-right: 6px;
          filter: drop-shadow(0 2px 8px #fff) drop-shadow(0 0 8px #a259ff33);
        }
        @media (max-width: 600px) {
          .student-back-home-btn {
            top: 12px;
            left: 8px;
            padding: 10px 18px 10px 14px;
            font-size: 0.98rem;
          }
        }
        .student-centered-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 70vh;
          width: 100%;
        }
        .student-title {
          font-family: "PP Editorial", serif;
          font-size: clamp(3rem, 6vw, 5rem);
          font-weight: 400;
          text-align: center;
          color: #23273a;
          margin-bottom: 30px;
          letter-spacing: -0.05em;
          line-height: 1.05;
        }
        .student-title-gradient {
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .student-subtitle {
          font-family: "FK Grotesk", sans-serif;
          font-size: clamp(1.1rem, 2.5vw, 1.4rem);
          color: #8a8e9c;
          margin-bottom: 50px;
          font-weight: 400;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
          text-align: center;
          line-height: 1.6;
        }
        .student-cta {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 20px 44px;
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          border: none;
          border-radius: 50px;
          font-family: "FK Grotesk", sans-serif;
          font-weight: 600;
          font-size: 20px;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 10px 30px rgba(79, 140, 255, 0.18);
        }
        .student-cta:hover {
          transform: translateY(-3px);
          box-shadow: 0 20px 40px rgba(79, 140, 255, 0.22);
        }
        .student-platform-header {
          background: rgba(250, 250, 247, 0.98);
          border-bottom: 1px solid rgba(0, 0, 0, 0.07);
          padding: 24px 0 16px 0;
        }
        .student-back-btn {
          color: #8a8e9c;
          font-size: 15px;
          font-family: "FK Grotesk", sans-serif;
        }
        .student-back-btn:hover {
          color: #23273a;
        }
        .student-progress-container {
          margin-top: 10px;
        }
        .student-progress-bar {
          background: rgba(0, 0, 0, 0.08);
        }
        .student-progress-fill {
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          box-shadow: 0 0 10px rgba(79, 140, 255, 0.18);
        }
        .student-progress-percentage {
          color: #a259ff;
          font-weight: 700;
          font-family: "FK Grotesk", sans-serif;
        }
        .student-form-container {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px 40px; // 120px 20px 40px
        }
        .student-form-content {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .platform-step,
        .platform-completion-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          max-width: 600px;
          margin: 0 auto;
          padding: 40px 0 20px 0;
        }
        .platform-step-header {
          width: 100%;
          margin-bottom: 30px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .platform-step-number {
          color: #a259ff;
          font-size: 1.2rem;
          font-weight: 600;
          display: block;
          margin-bottom: 10px;
        }
        .platform-step-title {
          font-family: "PP Editorial", serif;
          font-size: 2.2rem;
          font-weight: 400;
          color: #23273a;
          margin-bottom: 0;
        }
        .platform-step-subtitle {
          font-size: 1.2rem;
          color: #8a8e9c;
          margin-bottom: 30px;
          text-align: center;
          line-height: 1.6;
        }
        .platform-step-instruction {
          color: #8a8e9c;
          font-size: 1.1rem;
          margin-bottom: 40px;
          text-align: center;
        }
        .platform-select {
          width: 100%;
          padding: 15px;
          background: rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: 10px;
          color: #23273a;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .platform-select:focus {
          outline: none;
          border-color: #4f8cff;
          background: rgba(255, 255, 255, 0.9);
          transform: translateY(-2px);
        }
        .platform-options-grid {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .platform-option-btn {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 20px 25px;
          background: rgba(255, 255, 255, 0.6);
          border: 2px solid rgba(0, 0, 0, 0.1);
          border-radius: 12px;
          color: #23273a;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          text-align: left;
        }
        .platform-option-btn:hover {
          background: rgba(255, 255, 255, 0.8);
          border-color: rgba(79, 140, 255, 0.3);
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }
        .platform-option-btn.selected {
          background: rgba(79, 140, 255, 0.15);
          border-color: #4f8cff;
          transform: translateY(-2px);
        }
        .platform-option-letter {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.9rem;
          flex-shrink: 0;
          transition: all 0.3s ease;
        }
        .platform-option-btn.selected .platform-option-letter {
          background: #4f8cff;
          color: #fff;
        }
        .student-form-navigation {
          display: flex;
          gap: 24px;
          margin-top: 40px;
          width: 100%;
          max-width: 600px;
          justify-content: center;
        }
        .student-nav-btn-prev {
          background: transparent;
          color: #8a8e9c;
          border: 1.5px solid rgba(0, 0, 0, 0.18);
          font-family: "FK Grotesk", sans-serif;
        }
        .student-nav-btn-prev:hover {
          color: #23273a;
          border-color: #a259ff;
          transform: translateY(-2px);
        }
        .student-nav-btn-next {
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          font-family: "FK Grotesk", sans-serif;
          margin-left: auto;
          border: none;
        }
        .student-nav-btn-next:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(162, 89, 255, 0.13);
        }
        .platform-nav-hint {
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.85rem;
        }
        .platform-completion-step {
          text-align: center;
          max-width: 500px;
        }
        .platform-success-icon {
          margin-bottom: 30px;
          animation: successPulse 1s ease;
        }
        .platform-success-icon svg {
          color: #30d158;
        }
        .platform-submission-note {
          color: #8a8e9c;
          font-size: 0.9rem;
          margin-bottom: 30px;
          font-style: italic;
        }
        .platform-continue-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 18px 36px;
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          border: none;
          border-radius: 50px;
          font-weight: 600;
          font-size: 18px;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .platform-continue-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 20px 40px rgba(79, 140, 255, 0.4);
        }
        @keyframes successPulse {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        .platform-options-grid.student-subjects-grid {
          display: grid !important;
          grid-template-columns: 1fr 1fr;
          gap: 16px 18px;
          margin-bottom: 8px;
        }
        @media (max-width: 700px) {
          .platform-options-grid.student-subjects-grid {
            grid-template-columns: 1fr;
          }
        }
        .student-subject-btn {
          min-width: 0;
          width: 100%;
          max-width: 240px;
          padding: 14px 12px;
          font-size: 1.01rem;
        }
        .zentora-floating-logo.student-floating-logo {
          position: fixed;
          top: 110px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 2000;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 160px;
          height: 160px;
          pointer-events: none;
          animation: zentoraLogoFloat 3.5s ease-in-out infinite alternate;
        }
        .zentora-floating-logo.student-floating-logo img {
          width: 120px;
          height: 120px;
          object-fit: contain;
          filter: drop-shadow(0 8px 32px #a259ff44) drop-shadow(0 0 12px #fff8);
          opacity: 0.98;
        }
        @keyframes zentoraLogoFloat {
          0% {
            transform: translateX(-50%) translateY(0);
          }
          100% {
            transform: translateX(-50%) translateY(-18px);
          }
        }
        @media (max-width: 700px) {
          .zentora-floating-logo.student-floating-logo {
            top: 16px;
            width: 90px;
            height: 90px;
          }
          .zentora-floating-logo.student-floating-logo img {
            width: 64px;
            height: 64px;
          }
        }
        /* Tutor-style navigation buttons for student form */
        .tutor-form-action-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 16px 36px;
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          border: none;
          border-radius: 50px;
          font-family: "FK Grotesk", sans-serif;
          font-weight: 600;
          font-size: 1.08rem;
          box-shadow: 0 4px 24px #a259ff33, 0 1.5px 6px #4f8cff22;
          cursor: pointer;
          transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
          outline: none;
        }
        .tutor-form-action-btn:hover,
        .tutor-form-action-btn:focus {
          background: linear-gradient(90deg, #a259ff 0%, #4f8cff 100%);
          box-shadow: 0 8px 32px #a259ff55, 0 2px 12px #4f8cff33;
          transform: translateY(-2px) scale(1.04);
        }
        .tutor-form-action-btn.disabled,
        .tutor-form-action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          box-shadow: none;
          background: linear-gradient(90deg, #bdbdbd 0%, #e0e0e0 100%);
        }
        .tutor-form-action-icon {
          font-size: 1.2em;
          display: inline-flex;
          align-items: center;
          filter: drop-shadow(0 2px 8px #fff) drop-shadow(0 0 8px #a259ff33);
        }
        .tutor-form-action-back-icon {
          margin-right: 6px;
        }
        .tutor-form-action-next-icon {
          margin-left: 6px;
        }
      `}</style>
    </div>
  ) : (
    <MatchResults
      matches={matchResults}
      setCurrentStep={setCurrentStep}
      setShowStudentPlatform={setShowStudentPlatform}
    />
  );
};

// Tutor Platform Component
const TutorPlatform = ({
  currentStep,
  formData,
  handleFormUpdate,
  nextStep,
  prevStep,
  setShowTutorPlatform,
  setCurrentStep,
}) => {
  const handleMultiSelect = (field, value) => {
    const current = formData[field] || [];
    const updated = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    handleFormUpdate(field, updated);
  };

  const [otherLanguage, setOtherLanguage] = useState("");
  const [showOtherLanguageInput, setShowOtherLanguageInput] = useState(false);

  const handleOtherLanguage = () => {
    const standardLanguages = ["English", "Spanish", "Mandarin", "Hindi", "French"];
    const filteredLanguages = (formData.languagePreference || []).filter((lang) => standardLanguages.includes(lang));

    if (showOtherLanguageInput) {
      // Remove other langauge input
      setShowOtherLanguageInput(false);
      setOtherLanguage("");
      handleFormUpdate("languagePreference", filteredLanguages);
    } else {
      setShowOtherLanguageInput(true);
    }
  };

  const handleOtherLanguageChange = (value) => {
    const trimmedValue = value.trim();
    const formattedValue =
      trimmedValue.length > 1
        ? trimmedValue.charAt(0).toUpperCase() + trimmedValue.slice(1).toLowerCase()
        : trimmedValue.toUpperCase();
    setOtherLanguage(formattedValue);
    const standardLanguages = ["English", "Spanish", "Mandarin", "Hindi", "French"];
    const filteredLanguages = (formData.languagePreference || []).filter((lang) => standardLanguages.includes(lang));

    handleFormUpdate("languagePreference", formattedValue ? [...filteredLanguages, formattedValue] : filteredLanguages);
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 0:
        return true;
      case 1:
        return formData.firstName.trim() !== "" && formData.lastName.trim() !== "" && formData.email.trim() !== "";
      case 2:
        return formData.timezone !== "";
      case 3:
        return formData.subjectPreferences && formData.subjectPreferences.length > 0;
      case 4:
        return formData.teachingStyle && formData.teachingStyle.length > 0;
      case 5:
        return formData.teachingPace !== "";
      case 6:
        return formData.sessionFormat && formData.sessionFormat.length > 0;
      case 7:
        return formData.experienceLevel !== "";
      case 8:
        return formData.tutoringApproach !== "";
      case 9:
        return formData.availabilityFlexibility !== "";
      case 10:
        return formData.languagePreference && formData.languagePreference.length > 0;
      case 11:
        return formData.tutorBackground && formData.tutorBackground.length > 0;
      case 12:
        return formData.helpFocus !== "";
      case 13:
        return !!formData.idVerification;
      case 14:
        return !!formData.transcript;
      case 15:
        return !!formData.profilePhoto;
      case 16:
        return !!formData.introVideo;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    const submissionData = {
      submissionId: `TUTOR_${Date.now()}`,
      timestamp: new Date().toISOString(),
      tutorProfile: {
        contact: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
        },
        verification: {
          idVerification: formData.idVerification,
          transcript: formData.transcript,
        },
        media: {
          profilePhoto: formData.profilePhoto,
          introVideo: formData.introVideo,
        },
        preferences: {
          timezone: formData.timezone,
          subjectPreferences: formData.subjectPreferences,
          teachingStyle: formData.teachingStyle,
          teachingPace: formData.teachingPace,
          sessionFormat: formData.sessionFormat,
          experienceLevel: formData.experienceLevel,
          tutoringApproach: formData.tutoringApproach,
          availabilityFlexibility: formData.availabilityFlexibility,
          languagePreference: formData.languagePreference,
          tutorBackground: formData.tutorBackground,
          helpFocus: formData.helpFocus,
        },
      },
      status: "submitted",
      completionRate: "100%",
    };

    console.log("=== ZENTORA TUTOR INFORMATION SUBMITTED ===");
    console.log(JSON.stringify(submissionData, null, 2));
    console.log("=== END SUBMISSION ===");

    try {
      const response = await fetch("https://zentora-backend-dd6w.onrender.com/api/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ SUCCESS! Tutor information submitted to database:", result);
    } catch (err) {
      console.error("❌ Tutor submission failed:", err);
      alert("❌ Tutor submission failed: " + err.message);
    }

    nextStep();
  };

  const steps = [
    // Step 0: Welcome
    {
      component: (
        <div className="platform-welcome-step tutor-centered-step">
          <h1 className="platform-welcome-title tutor-title">
            Join the
            <br />
            <span className="platform-gradient-text tutor-title-gradient">Zentora Team</span>
          </h1>
          <p className="platform-welcome-subtitle tutor-subtitle">
            Join our elite peer tutoring platform and start making a meaningful impact on student learning.
          </p>
          <button className="platform-welcome-cta tutor-cta" onClick={nextStep}>
            Begin Onboarding
            <span className="platform-btn-arrow">→</span>
          </button>
        </div>
      ),
    },
    // Step 1: Name & Email
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">1 →</span>
            <h2 className="platform-step-title">Your Details</h2>
          </div>
          <p className="platform-step-instruction">Please enter your name and email address.</p>
          <div style={{ width: "100%", maxWidth: 400, display: "flex", flexDirection: "column", gap: 18 }}>
            <input
              className="platform-select"
              type="text"
              placeholder="First Name"
              value={formData.firstName}
              onChange={(e) => handleFormUpdate("firstName", e.target.value.trim())}
              style={{ marginBottom: 12 }}
            />
            <input
              className="platform-select"
              type="text"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={(e) => handleFormUpdate("lastName", e.target.value.trim())}
              style={{ marginBottom: 12 }}
            />
            <input
              className="platform-select"
              type="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={(e) => handleFormUpdate("email", e.target.value.trim())}
            />
          </div>
        </div>
      ),
    },
    // Step 2: Timezone
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">2 →</span>
            <h2 className="platform-step-title">Your Timezone</h2>
          </div>
          <p className="platform-step-instruction">Select your current timezone</p>
          <select
            className="platform-select"
            value={formData.timezone || ""}
            onChange={(e) => handleFormUpdate("timezone", e.target.value)}>
            <option value="">Select timezone...</option>
            <option value="PT">Pacific Time (PT)</option>
            <option value="MT">Mountain Time (MT)</option>
            <option value="CT">Central Time (CT)</option>
            <option value="ET">Eastern Time (ET)</option>
            <option value="GMT">GMT</option>
            <option value="CET">Central European Time (CET)</option>
            <option value="IST">India Standard Time (IST)</option>
            <option value="JST">Japan Standard Time (JST)</option>
            <option value="AEST">Australian Eastern Time (AEST)</option>
          </select>
        </div>
      ),
    },
    // Step 3: Subject Preferences (conversational, adaptive)
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">3 →</span>
            <h2 className="platform-step-title">Subjects</h2>
          </div>
          <div className="tutor-chat-bubble">
            <div className="tutor-chat-question">Which subjects are you most passionate about tutoring?</div>
            <div className="tutor-chat-helper tutor-chat-instruction">
              Select all that apply. We'll use this to match you with students who need your expertise.
            </div>
            <div className="platform-options-grid tutor-chat-options tutor-subjects-grid">
              {[
                {
                  subject: "Mathematics",
                  icon: (
                    <svg width="28" height="28" viewBox="0 0 28 28">
                      <text x="14" y="19" textAnchor="middle" fontSize="20" fontFamily="serif" fill="#23273a">
                        ∑
                      </text>
                    </svg>
                  ),
                },
                {
                  subject: "Physics",
                  icon: (
                    <svg width="28" height="28" viewBox="0 0 28 28">
                      <circle cx="14" cy="14" r="9" stroke="#23273a" strokeWidth="2" fill="none" />
                      <circle cx="14" cy="8" r="2" fill="#a259ff" />
                      <circle cx="20" cy="16" r="1.5" fill="#4f8cff" />
                      <circle cx="9" cy="18" r="1.2" fill="#a259ff" />
                    </svg>
                  ),
                },
                {
                  subject: "Chemistry",
                  icon: (
                    <svg width="28" height="28" viewBox="0 0 28 28">
                      <rect x="9" y="7" width="10" height="12" rx="3" stroke="#23273a" strokeWidth="2" fill="none" />
                      <circle cx="14" cy="19" r="2" fill="#a259ff" />
                    </svg>
                  ),
                },
                {
                  subject: "Biology",
                  icon: (
                    <svg width="28" height="28" viewBox="0 0 28 28">
                      <ellipse cx="14" cy="14" rx="8" ry="4" stroke="#23273a" strokeWidth="2" fill="none" />
                      <path d="M10 10 Q14 18 18 10" stroke="#a259ff" strokeWidth="1.5" fill="none" />
                    </svg>
                  ),
                },
                {
                  subject: "Computer Science",
                  icon: (
                    <svg width="28" height="28" viewBox="0 0 28 28">
                      <text x="14" y="19" textAnchor="middle" fontSize="18" fontFamily="monospace" fill="#23273a">
                        {"{}"}
                      </text>
                    </svg>
                  ),
                },
                {
                  subject: "English",
                  icon: (
                    <svg width="28" height="28" viewBox="0 0 28 28">
                      <rect x="7" y="8" width="14" height="12" rx="2" stroke="#23273a" strokeWidth="2" fill="none" />
                      <line x1="10" y1="12" x2="18" y2="12" stroke="#a259ff" strokeWidth="1.5" />
                    </svg>
                  ),
                },
                {
                  subject: "History",
                  icon: (
                    <svg width="28" height="28" viewBox="0 0 28 28">
                      <rect x="9" y="8" width="10" height="12" rx="2" stroke="#23273a" strokeWidth="2" fill="none" />
                      <line x1="14" y1="8" x2="14" y2="20" stroke="#a259ff" strokeWidth="1.5" />
                    </svg>
                  ),
                },
                {
                  subject: "Economics",
                  icon: (
                    <svg width="28" height="28" viewBox="0 0 28 28">
                      <polyline points="7,19 14,9 21,19" stroke="#23273a" strokeWidth="2" fill="none" />
                      <circle cx="14" cy="9" r="1.5" fill="#a259ff" />
                    </svg>
                  ),
                },
                {
                  subject: "Psychology",
                  icon: (
                    <svg width="28" height="28" viewBox="0 0 28 28">
                      <text x="14" y="19" textAnchor="middle" fontSize="18" fontFamily="serif" fill="#23273a">
                        Ψ
                      </text>
                    </svg>
                  ),
                },
                {
                  subject: "Engineering",
                  icon: (
                    <svg width="28" height="28" viewBox="0 0 28 28">
                      <circle cx="14" cy="14" r="8" stroke="#23273a" strokeWidth="2" fill="none" />
                      <circle cx="14" cy="14" r="3" fill="#a259ff" />
                      <rect x="13" y="6" width="2" height="4" fill="#4f8cff" />
                      <rect x="13" y="18" width="2" height="4" fill="#4f8cff" />
                    </svg>
                  ),
                },
              ].map(({ subject, icon }) => (
                <button
                  key={subject}
                  className={`platform-option-btn tutor-subject-btn ${
                    (formData.subjectPreferences || []).includes(subject) ? "selected" : ""
                  }`}
                  onClick={() => handleMultiSelect("subjectPreferences", subject)}
                  aria-pressed={(formData.subjectPreferences || []).includes(subject)}>
                  <span className="platform-option-letter platform-option-icon">{icon}</span>
                  {subject}
                </button>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    // Step 4: Teaching Style
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">4 →</span>
            <h2 className="platform-step-title">Teaching Style</h2>
          </div>
          <p className="platform-step-instruction">How do you prefer to teach? Select all that apply.</p>
          <div className="platform-options-grid">
            {[
              {
                value: "Visual",
                label: "Visual explanations",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <ellipse cx="14" cy="14" rx="10" ry="6" stroke="#23273a" strokeWidth="2" />
                    <circle cx="14" cy="14" r="2.5" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Interactive",
                label: "Lectures/Interactive discussions",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="5" y="8" width="18" height="10" rx="4" stroke="#23273a" strokeWidth="2" />
                    <path d="M9 18l2-2h6l2 2" stroke="#a259ff" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                ),
              },
              {
                value: "Hands-on",
                label: "Hands-on practice",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="8" y="10" width="12" height="8" rx="2" stroke="#23273a" strokeWidth="2" />
                    <path d="M10 18v2h8v-2" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
              {
                value: "Conceptual",
                label: "Conceptual deep-dives",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="8" stroke="#23273a" strokeWidth="2" />
                    <path d="M14 6v16M6 14h16" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
              {
                value: "Problem-solving",
                label: "Problem-solving focused",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="7" y="7" width="14" height="14" rx="4" stroke="#23273a" strokeWidth="2" />
                    <path d="M11 17l6-6M11 11h6v6" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
            ].map((style, index) => (
              <button
                key={style.value}
                className={`platform-option-btn ${
                  (formData.teachingStyle || []).includes(style.value) ? "selected" : ""
                }`}
                onClick={() => handleMultiSelect("teachingStyle", style.value)}>
                <span className="platform-option-letter platform-option-icon">{style.icon}</span>
                {style.label}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    // Step 5: Teaching Pace
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">5 →</span>
            <h2 className="platform-step-title">Preferred Teaching Pace</h2>
          </div>
          <p className="platform-step-instruction">What pace do you prefer when teaching?</p>
          <div className="platform-options-grid">
            {[
              {
                value: "Slow",
                label: "Slow and thorough",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="10" stroke="#23273a" strokeWidth="2" />
                    <path d="M14 8v6l-4 4" stroke="#a259ff" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                ),
              },
              {
                value: "Moderate",
                label: "Moderate pace",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="10" stroke="#23273a" strokeWidth="2" />
                    <path d="M14 8v6l2.5 2.5" stroke="#a259ff" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                ),
              },
              {
                value: "Fast-paced",
                label: "Fast-paced",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="10" stroke="#23273a" strokeWidth="2" />
                    <path d="M14 8v6l4 4" stroke="#a259ff" strokeWidth="2" strokeLinecap="round" />
                    <path d="M18 18l2 2" stroke="#a259ff" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                ),
              },
            ].map((pace, index) => (
              <button
                key={pace.value}
                className={`platform-option-btn ${formData.teachingPace === pace.value ? "selected" : ""}`}
                onClick={() => handleFormUpdate("teachingPace", pace.value)}>
                <span className="platform-option-letter platform-option-icon">{pace.icon}</span>
                {pace.label}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    // Step 6: Session Format
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">6 →</span>
            <h2 className="platform-step-title">Session Format Preference</h2>
          </div>
          <p className="platform-step-instruction">Which format(s) are you comfortable with? Select all that apply.</p>
          <div className="platform-options-grid">
            {[
              {
                value: "Text-based",
                label: "Text-based (chat)",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="5" y="8" width="18" height="10" rx="4" stroke="#23273a" strokeWidth="2" />
                    <path d="M9 18l2-2h6l2 2" stroke="#a259ff" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                ),
              },
              {
                value: "Video call",
                label: "Video call",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="6" y="9" width="12" height="10" rx="3" stroke="#23273a" strokeWidth="2" />
                    <polygon points="20,12 24,14 20,16" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Screen sharing",
                label: "Screen sharing",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="5" y="8" width="18" height="10" rx="2" stroke="#23273a" strokeWidth="2" />
                    <rect x="11" y="14" width="6" height="2" rx="1" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Collaborative whiteboard",
                label: "Collaborative whiteboard",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="6" y="8" width="16" height="10" rx="2" stroke="#23273a" strokeWidth="2" />
                    <path d="M9 16l4-4 4 4" stroke="#a259ff" strokeWidth="1.5" strokeLinecap="round" />
                    <circle cx="14" cy="14" r="1.5" fill="#a259ff" />
                  </svg>
                ),
              },
            ].map((format, index) => (
              <button
                key={format.value}
                className={`platform-option-btn ${
                  (formData.sessionFormat || []).includes(format.value) ? "selected" : ""
                }`}
                onClick={() => handleMultiSelect("sessionFormat", format.value)}>
                <span className="platform-option-letter platform-option-icon">{format.icon}</span>
                {format.label}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    // Step 7: Experience Level
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">7 →</span>
            <h2 className="platform-step-title">Your Tutoring Experience</h2>
          </div>
          <p className="platform-step-instruction">What's your tutoring experience level?</p>
          <div className="platform-options-grid">
            {[
              {
                value: "Beginner",
                label: "New to tutoring",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="9" stroke="#23273a" strokeWidth="2" />
                    <circle cx="14" cy="14" r="3" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Intermediate",
                label: "Some tutoring experience",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="9" stroke="#23273a" strokeWidth="2" />
                    <circle cx="14" cy="11" r="2" fill="#a259ff" />
                    <circle cx="14" cy="17" r="2" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Advanced",
                label: "Extensive tutoring experience",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="9" stroke="#23273a" strokeWidth="2" />
                    <circle cx="10" cy="14" r="2" fill="#a259ff" />
                    <circle cx="18" cy="14" r="2" fill="#a259ff" />
                    <circle cx="14" cy="10" r="2" fill="#a259ff" />
                  </svg>
                ),
              },
            ].map((level, index) => (
              <button
                key={level.value}
                className={`platform-option-btn ${formData.experienceLevel === level.value ? "selected" : ""}`}
                onClick={() => handleFormUpdate("experienceLevel", level.value)}>
                <span className="platform-option-letter platform-option-icon">{level.icon}</span>
                {level.label}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    // Step 8: Tutoring Approach
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">8 →</span>
            <h2 className="platform-step-title">Tutoring Approach</h2>
          </div>
          <p className="platform-step-instruction">How do you prefer to structure sessions?</p>
          <div className="platform-options-grid">
            {[
              {
                value: "Structured",
                label: "Structured lesson plans",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="7" y="8" width="14" height="12" rx="3" stroke="#23273a" strokeWidth="2" />
                    <path d="M10 12h8M10 16h8" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
              {
                value: "Flexible",
                label: "Flexible/adaptive approach",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="7" y="8" width="14" height="12" rx="3" stroke="#23273a" strokeWidth="2" />
                    <path d="M10 14c2-2 6 2 8 0" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
            ].map((approach, index) => (
              <button
                key={approach.value}
                className={`platform-option-btn ${formData.tutoringApproach === approach.value ? "selected" : ""}`}
                onClick={() => handleFormUpdate("tutoringApproach", approach.value)}>
                <span className="platform-option-letter platform-option-icon">{approach.icon}</span>
                {approach.label}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    // Step 9: Availability
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">9 →</span>
            <h2 className="platform-step-title">Your Availability</h2>
          </div>
          <p className="platform-step-instruction">How flexible is your schedule?</p>
          <div className="platform-options-grid">
            {[
              {
                value: "Fixed schedule",
                label: "Fixed schedule",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="6" y="8" width="16" height="12" rx="3" stroke="#23273a" strokeWidth="2" />
                    <rect x="10" y="12" width="8" height="4" rx="1" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Flexible timing",
                label: "Flexible timing",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="6" y="8" width="16" height="12" rx="3" stroke="#23273a" strokeWidth="2" />
                    <path d="M10 16c2-2 6 2 8 0" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
            ].map((availability, index) => (
              <button
                key={availability.value}
                className={`platform-option-btn ${
                  formData.availabilityFlexibility === availability.value ? "selected" : ""
                }`}
                onClick={() => handleFormUpdate("availabilityFlexibility", availability.value)}>
                <span className="platform-option-letter platform-option-icon">{availability.icon}</span>
                {availability.label}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    // Step 10: Language Preference
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">10 →</span>
            <h2 className="platform-step-title">Language Preference</h2>
          </div>
          <p className="platform-step-instruction">Which language(s) can you tutor in? Select all that apply.</p>
          <div className="platform-options-grid">
            {[
              {
                value: "English",
                label: "English",
                icon: (
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <rect x="4" y="7" width="28" height="22" rx="6" stroke="#23273a" strokeWidth="2.2" fill="#fff" />
                    <text
                      x="18"
                      y="25"
                      textAnchor="middle"
                      fontSize="16"
                      fontFamily="sans-serif"
                      fill="#a259ff"
                      fontWeight="bold">
                      EN
                    </text>
                  </svg>
                ),
              },
              {
                value: "Spanish",
                label: "Spanish",
                icon: (
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <rect x="4" y="7" width="28" height="22" rx="6" stroke="#23273a" strokeWidth="2.2" fill="#fff" />
                    <text
                      x="18"
                      y="25"
                      textAnchor="middle"
                      fontSize="16"
                      fontFamily="sans-serif"
                      fill="#a259ff"
                      fontWeight="bold">
                      ES
                    </text>
                  </svg>
                ),
              },
              {
                value: "Mandarin",
                label: "Mandarin",
                icon: (
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <rect x="4" y="7" width="28" height="22" rx="6" stroke="#23273a" strokeWidth="2.2" fill="#fff" />
                    <text
                      x="18"
                      y="25"
                      textAnchor="middle"
                      fontSize="16"
                      fontFamily="sans-serif"
                      fill="#a259ff"
                      fontWeight="bold">
                      中
                    </text>
                  </svg>
                ),
              },
              {
                value: "Hindi",
                label: "Hindi",
                icon: (
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <rect x="4" y="7" width="28" height="22" rx="6" stroke="#23273a" strokeWidth="2.2" fill="#fff" />
                    <text
                      x="18"
                      y="25"
                      textAnchor="middle"
                      fontSize="16"
                      fontFamily="sans-serif"
                      fill="#a259ff"
                      fontWeight="bold">
                      हि
                    </text>
                  </svg>
                ),
              },
              {
                value: "French",
                label: "French",
                icon: (
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <rect x="4" y="7" width="28" height="22" rx="6" stroke="#23273a" strokeWidth="2.2" fill="#fff" />
                    <text
                      x="18"
                      y="25"
                      textAnchor="middle"
                      fontSize="16"
                      fontFamily="sans-serif"
                      fill="#a259ff"
                      fontWeight="bold">
                      FR
                    </text>
                  </svg>
                ),
              },
              {
                value: "Other",
                label: "Other",
                icon: (
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <rect x="4" y="7" width="28" height="22" rx="6" stroke="#23273a" strokeWidth="2.2" fill="#fff" />
                    <circle cx="18" cy="18" r="6" fill="#a259ff" />
                  </svg>
                ),
              },
            ].map((lang, index) => (
              <button
                key={lang.value}
                className={`platform-option-btn ${
                  (formData.languagePreference || []).includes(lang.value) ||
                  (lang.value === "Other" && otherLanguage !== "")
                    ? "selected"
                    : ""
                }`}
                onClick={() =>
                  lang.value === "Other" ? handleOtherLanguage() : handleMultiSelect("languagePreference", lang.value)
                }>
                <span
                  className="platform-option-letter platform-option-icon"
                  style={{ padding: "0 6px 0 0", minWidth: 40 }}>
                  {lang.icon}
                </span>
                {lang.label}
              </button>
            ))}
          </div>
          {showOtherLanguageInput && (
            <div>
              <div
                style={{
                  width: "90%",
                  maxWidth: 400,
                  display: "flex",
                  flexDirection: "column",
                  gap: 18,
                  marginTop: "10px",
                }}>
                <input
                  className="platform-select"
                  type="text"
                  placeholder="Enter other language"
                  value={otherLanguage}
                  onChange={(e) => handleOtherLanguageChange(e.target.value)}
                  style={{ marginBottom: 12 }}
                />
              </div>
            </div>
          )}
        </div>
      ),
    },
    // Step 11: Tutor Background
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">11 →</span>
            <h2 className="platform-step-title">Your Background</h2>
          </div>
          <p className="platform-step-instruction">What describes your academic background? Select all that apply.</p>
          <div className="platform-options-grid">
            {[
              {
                value: "UIUC Student",
                label: "UIUC Student",
                icon: (
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <rect x="6" y="14" width="20" height="10" rx="3" stroke="#23273a" strokeWidth="2" fill="#fff" />
                    <rect x="10" y="10" width="12" height="6" rx="2" stroke="#a259ff" strokeWidth="1.5" fill="#fff" />
                    <circle cx="16" cy="23" r="2" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Graduate Student",
                label: "Graduate Student",
                icon: (
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <ellipse cx="16" cy="18" rx="8" ry="4" stroke="#23273a" strokeWidth="2" fill="#fff" />
                    <path d="M16 10v8" stroke="#a259ff" strokeWidth="1.5" />
                    <polygon points="12,10 16,6 20,10" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Recent Graduate",
                label: "Recent Graduate",
                icon: (
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <rect x="6" y="14" width="20" height="10" rx="3" stroke="#23273a" strokeWidth="2" fill="#fff" />
                    <path d="M10 24l12-12" stroke="#a259ff" strokeWidth="1.5" />
                    <polygon points="22,12 24,10 26,12 24,14" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Working Professional",
                label: "Working Professional",
                icon: (
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <rect x="8" y="16" width="16" height="8" rx="2" stroke="#23273a" strokeWidth="2" fill="#fff" />
                    <rect x="12" y="12" width="8" height="6" rx="1.5" stroke="#a259ff" strokeWidth="1.5" fill="#fff" />
                    <rect x="14" y="20" width="4" height="4" rx="1" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Teaching Assistant",
                label: "Teaching Assistant",
                icon: (
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <rect x="6" y="14" width="20" height="10" rx="3" stroke="#23273a" strokeWidth="2" fill="#fff" />
                    <rect x="10" y="10" width="12" height="6" rx="2" stroke="#a259ff" strokeWidth="1.5" fill="#fff" />
                    <rect x="14" y="18" width="4" height="2" rx="1" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Research Experience",
                label: "Research Experience",
                icon: (
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <ellipse cx="16" cy="20" rx="8" ry="4" stroke="#23273a" strokeWidth="2" fill="#fff" />
                    <rect x="14" y="8" width="4" height="10" rx="2" fill="#a259ff" />
                    <rect x="14" y="8" width="4" height="2" rx="1" fill="#fff" />
                  </svg>
                ),
              },
            ].map((background, index) => (
              <button
                key={background.value}
                className={`platform-option-btn ${
                  (formData.tutorBackground || []).includes(background.value) ? "selected" : ""
                }`}
                onClick={() => handleMultiSelect("tutorBackground", background.value)}>
                <span
                  className="platform-option-letter platform-option-icon"
                  style={{ padding: "0 6px 0 0", minWidth: 40 }}>
                  {background.icon}
                </span>
                {background.label}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    // Step 12: Help Focus
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">12 →</span>
            <h2 className="platform-step-title">Teaching Focus</h2>
          </div>
          <p className="platform-step-instruction">What type of help do you prefer to provide?</p>
          <div className="platform-options-grid">
            {[
              {
                value: "Assignment/Homework",
                label: "Assignment/Homework help",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="8" y="8" width="12" height="12" rx="2" stroke="#23273a" strokeWidth="2" />
                    <path d="M12 12h4M12 16h4" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
              {
                value: "Conceptual",
                label: "Long-term concept building",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="8" stroke="#23273a" strokeWidth="2" />
                    <path d="M10 18c2-2 6 2 8 0" stroke="#a259ff" strokeWidth="1.5" />
                  </svg>
                ),
              },
              {
                value: "Exam preparation",
                label: "Exam preparation",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="8" y="8" width="12" height="12" rx="2" stroke="#23273a" strokeWidth="2" />
                    <path d="M14 12v4" stroke="#a259ff" strokeWidth="1.5" />
                    <circle cx="14" cy="16" r="1.5" fill="#a259ff" />
                  </svg>
                ),
              },
              {
                value: "Project",
                label: "Project assistance",
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="8" y="8" width="12" height="12" rx="2" stroke="#23273a" strokeWidth="2" />
                    <path d="M12 16l4-4" stroke="#a259ff" strokeWidth="1.5" />
                    <circle cx="16" cy="12" r="1.5" fill="#a259ff" />
                  </svg>
                ),
              },
            ].map((focus, index) => (
              <button
                key={focus.value}
                className={`platform-option-btn ${formData.helpFocus === focus.value ? "selected" : ""}`}
                onClick={() => handleFormUpdate("helpFocus", focus.value)}>
                <span className="platform-option-letter platform-option-icon">{focus.icon}</span>
                {focus.label}
              </button>
            ))}
          </div>
        </div>
      ),
    },

    // Step 13: ID Verification Upload
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">13 →</span>
            <h2 className="platform-step-title">ID Verification</h2>
          </div>
          <p className="platform-step-instruction">Upload a government-issued ID (PDF, JPG, PNG).</p>
          <input
            className="platform-select"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                handleFormUpdate("idVerification", {
                  fileName: file.name,
                  fileSize: file.size,
                  fileType: file.type,
                });
              }
            }}
          />
          {formData.idVerification && (
            <div style={{ marginTop: 10, color: "#4f8cff" }}>
              Uploaded: {formData.idVerification.fileName} ({(formData.idVerification.fileSize / 1024).toFixed(1)} KB)
            </div>
          )}
        </div>
      ),
    },
    // Step 14: Transcript Upload
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">14 →</span>
            <h2 className="platform-step-title">Transcript</h2>
          </div>
          <p className="platform-step-instruction">Upload your transcript (PDF, JPG, PNG).</p>
          <input
            className="platform-select"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                handleFormUpdate("transcript", {
                  fileName: file.name,
                  fileSize: file.size,
                  fileType: file.type,
                });
              }
            }}
          />
          {formData.transcript && (
            <div style={{ marginTop: 10, color: "#4f8cff" }}>
              Uploaded: {formData.transcript.fileName} ({(formData.transcript.fileSize / 1024).toFixed(1)} KB)
            </div>
          )}
        </div>
      ),
    },
    // Step 15: Profile Photo Upload
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">15 →</span>
            <h2 className="platform-step-title">Profile Photo</h2>
          </div>
          <p className="platform-step-instruction">Upload a profile photo (JPG, PNG).</p>
          <input
            className="platform-select"
            type="file"
            accept=".jpg,.jpeg,.png"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                handleFormUpdate("profilePhoto", {
                  fileName: file.name,
                  fileSize: file.size,
                  fileType: file.type,
                });
              }
            }}
          />
          {formData.profilePhoto && (
            <div style={{ marginTop: 10, color: "#4f8cff" }}>
              Uploaded: {formData.profilePhoto.fileName} ({(formData.profilePhoto.fileSize / 1024).toFixed(1)} KB)
            </div>
          )}
        </div>
      ),
    },
    // Step 16: Intro Video Link
    {
      component: (
        <div className="platform-step">
          <div className="platform-step-header">
            <span className="platform-step-number">16 →</span>
            <h2 className="platform-step-title">Intro Video</h2>
          </div>
          <p className="platform-step-instruction">Paste a link to your intro video (YouTube, Vimeo, etc.).</p>
          <input
            className="platform-select"
            type="url"
            placeholder="https://youtu.be/example"
            value={formData.introVideo}
            onChange={(e) => handleFormUpdate("introVideo", e.target.value.trim())}
          />
        </div>
      ),
    },

    // Step 17: Completion
    {
      component: (
        <div className="platform-completion-step">
          <div className="platform-success-icon">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h1 className="platform-step-title">Onboarding Complete!</h1>
          <p className="platform-step-subtitle">
            Thank you for completing the onboarding process. Your information has been successfully recorded.
          </p>
          {/* No Back to Home button here */}
        </div>
      ),
    },
  ];

  const currentStepData = steps[currentStep] || steps[0];
  const progress = (currentStep / (steps.length - 1)) * 100;

  // Animated transitions: track previous step for direction
  const [prevStepIdx, setPrevStepIdx] = useState(currentStep);
  useEffect(() => {
    setPrevStepIdx(currentStep);
  }, [currentStep]);

  // Summary sidebar data
  const summaryData = [
    { label: "Timezone", value: formData.timezone },
    { label: "Subjects", value: (formData.subjectPreferences || []).join(", ") },
    { label: "Teaching Style", value: (formData.teachingStyle || []).join(", ") },
    { label: "Teaching Pace", value: formData.teachingPace },
    { label: "Session Format", value: (formData.sessionFormat || []).join(", ") },
    { label: "Experience Level", value: formData.experienceLevel },
    { label: "Tutoring Approach", value: formData.tutoringApproach },
    { label: "Availability", value: formData.availabilityFlexibility },
    { label: "Languages", value: (formData.languagePreference || []).join(", ") },
    { label: "Background", value: (formData.tutorBackground || []).join(", ") },
    { label: "Help Focus", value: formData.helpFocus },
    { label: "ID Verification", value: formData.idVerification ? formData.idVerification.fileName : "" },
    { label: "Transcript", value: formData.transcript ? formData.transcript.fileName : "" },
    { label: "Profile Photo", value: formData.profilePhoto ? formData.profilePhoto.fileName : "" },
    { label: "Intro Video", value: formData.introVideo },
  ];

  return (
    <div className="zentora-platform tutor-platform-bg tutor-platform-flex" role="main">
      {/* Floating Zentora Logo (centered, on top of all content) */}
      {/* <div className="zentora-floating-logo">
        <img src="/zentora-transparent-white.png" alt="Zentora Logo" />
      </div> */}
      {/* Animated Background Visuals */}
      <div className="tutor-bg-visuals">
        <svg className="tutor-bg-blob" width="600" height="400" viewBox="0 0 600 400" fill="none">
          <ellipse cx="300" cy="200" rx="260" ry="140" fill="url(#tutorBlobGrad)" />
          <defs>
            <radialGradient id="tutorBlobGrad" cx="0.5" cy="0.5" r="0.7">
              <stop offset="0%" stopColor="#a259ff" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#4f8cff" stopOpacity="0.06" />
            </radialGradient>
          </defs>
        </svg>
        <svg className="tutor-bg-line" width="1000" height="120" viewBox="0 0 1000 120" fill="none">
          <defs>
            <linearGradient id="tutorLineGrad" x1="0" y1="0" x2="1000" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#a259ff" />
              <stop offset="100%" stopColor="#4f8cff" />
            </linearGradient>
          </defs>
          <path
            d="M0,100 Q500,200 1000,100"
            stroke="url(#tutorLineGrad)"
            strokeWidth="2.5"
            fill="none"
            opacity="0.13"
          />
        </svg>
      </div>
      {/* Floating Progress Bar */}
      <div className="tutor-floating-progress-bar">
        <div className="tutor-progress-bar-outer">
          <div className="tutor-progress-bar-inner" style={{ width: `${progress}%` }}></div>
        </div>
        <div className="tutor-progress-bar-label">{Math.round(progress)}%</div>
      </div>
      {/* Beautiful Back Home Button */}
      <button
        className="tutor-back-home-btn"
        onClick={() => {
          setShowTutorPlatform(false);
          setCurrentStep(0);
        }}
        aria-label="Back to Home">
        <span className="tutor-back-home-icon">←</span> Back to Home
      </button>
      {/* Persistent Summary Sidebar (desktop only) */}
      <aside className="tutor-summary-sidebar" aria-label="Application Summary">
        <h3 className="tutor-summary-title">Summary</h3>
        <ul className="tutor-summary-list">
          {summaryData.map((item, i) => (
            <li key={i} className={item.value ? "filled" : ""}>
              <span className="tutor-summary-label">{item.label}:</span>
              <span className="tutor-summary-value">
                {item.value || <span className="tutor-summary-missing">—</span>}
              </span>
            </li>
          ))}
        </ul>
      </aside>
      <div className="platform-form-container tutor-form-container">
        {/* Animated step transitions (slide/fade) */}
        <div
          className={`platform-form-content tutor-form-content tutor-step-animate tutor-step-slide`}
          key={currentStep}
          aria-live="polite">
          {currentStepData.component}
        </div>
        {currentStep > 0 && currentStep < steps.length - 1 && (
          <div className="platform-form-navigation tutor-form-navigation">
            {currentStep > 0 && (
              <button
                className="tutor-form-action-btn tutor-form-action-back"
                onClick={prevStep}
                aria-label="Previous Step"
                type="button">
                <span className="tutor-form-action-icon tutor-form-action-back-icon">←</span>
                Back
              </button>
            )}
            <button
              className={`tutor-form-action-btn tutor-form-action-next${isStepValid() ? "" : " disabled"}`}
              onClick={currentStep === steps.length - 2 ? handleSubmit : nextStep}
              aria-label={currentStep === steps.length - 2 ? "Submit" : "Next Step"}
              type="button"
              disabled={!isStepValid()}>
              {currentStep === steps.length - 2 ? "Submit" : "OK"}
              <span className="tutor-form-action-icon tutor-form-action-next-icon">→</span>
            </button>
          </div>
        )}
      </div>
      <style jsx>{`
        .zentora-floating-logo {
          position: absolute;
          top: 110px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 2000;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 160px;
          height: 160px;
          pointer-events: none;
          animation: zentoraLogoFloat 3.5s ease-in-out infinite alternate;
        }
        .zentora-floating-logo img {
          width: 120px;
          height: 120px;
          object-fit: contain;
          filter: drop-shadow(0 8px 32px #a259ff44) drop-shadow(0 0 12px #fff8);
          opacity: 0.98;
        }
        @keyframes zentoraLogoFloat {
          0% {
            transform: translateX(-50%) translateY(0);
          }
          100% {
            transform: translateX(-50%) translateY(-18px);
          }
        }
        @media (max-width: 700px) {
          .zentora-floating-logo {
            top: 16px;
            width: 90px;
            height: 90px;
          }
          .zentora-floating-logo img {
            width: 64px;
            height: 64px;
          }
        }
        .tutor-bg-visuals {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          z-index: 0;
          pointer-events: none;
          overflow: hidden;
        }
        .tutor-bg-blob {
          position: absolute;
          top: 12vh;
          left: 50vw;
          transform: translateX(-50%) scale(1.1);
          filter: blur(18px);
          opacity: 0.22;
          animation: tutorBlobFloat 16s ease-in-out infinite alternate;
        }
        @keyframes tutorBlobFloat {
          0% {
            transform: translateX(-50%) scale(1.1) translateY(0);
          }
          100% {
            transform: translateX(-50%) scale(1.13) translateY(-24px);
          }
        }
        .tutor-bg-line {
          position: absolute;
          top: 38vh;
          left: 0;
          width: 100vw;
          opacity: 0.18;
          animation: tutorLineMove 18s linear infinite alternate;
        }
        @keyframes tutorLineMove {
          0% {
            filter: hue-rotate(0deg);
          }
          100% {
            filter: hue-rotate(40deg);
          }
        }
        /* Slide/Fade Step Animation */
        .tutor-step-slide {
          animation: tutorStepSlideIn 0.7s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes tutorStepSlideIn {
          from {
            opacity: 0;
            transform: translateY(48px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: none;
          }
        }
        .tutor-platform-flex {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          justify-content: center;
          min-height: 100vh;
        }
        .tutor-summary-sidebar {
          position: fixed;
          top: 110px;
          left: 0;
          width: 320px;
          max-width: 32vw;
          background: rgba(255, 255, 255, 0.82);
          border-radius: 24px;
          box-shadow: 0 8px 40px #a259ff18, 0 0 0 2px #4f8cff11;
          padding: 32px 28px 24px 32px;
          margin-left: 24px;
          z-index: 100;
          font-family: "FK Grotesk", sans-serif;
          display: flex;
          flex-direction: column;
          gap: 12px;
          min-height: 420px;
          pointer-events: auto;
        }
        .tutor-summary-title {
          font-family: "PP Editorial", serif;
          font-size: 1.4rem;
          font-weight: 500;
          margin-bottom: 18px;
          color: #23273a;
        }
        .tutor-summary-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .tutor-summary-label {
          color: #8a8e9c;
          font-size: 1.01rem;
          font-weight: 500;
          margin-right: 8px;
        }
        .tutor-summary-value {
          color: #23273a;
          font-size: 1.01rem;
          font-weight: 500;
        }
        .tutor-summary-missing {
          color: #d0cfff;
        }
        .tutor-summary-list li.filled .tutor-summary-label {
          color: #4f8cff;
        }
        .tutor-summary-list li.filled .tutor-summary-value {
          color: #23273a;
        }
        @media (max-width: 1100px) {
          .tutor-summary-sidebar {
            display: none;
          }
        }
        /* Animated step transitions */
        .tutor-step-animate {
          animation: tutorStepFadeIn 0.7s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes tutorStepFadeIn {
          from {
            opacity: 0;
            transform: translateY(32px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: none;
          }
        }
        /* Tutor Platform Custom Styles */
        .tutor-platform-bg {
          background: linear-gradient(120deg, #f3f3f7 0%, #e9e6ff 100%);
          min-height: 100vh;
          font-family: "FK Grotesk", sans-serif;
        }
        .tutor-centered-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 70vh;
          width: 100%;
        }
        .tutor-title {
          font-family: "PP Editorial", serif;
          font-size: clamp(3rem, 6vw, 5rem);
          font-weight: 400;
          text-align: center;
          color: #23273a;
          margin-bottom: 30px;
          letter-spacing: -0.05em;
          line-height: 1.05;
        }
        .tutor-title-gradient {
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .tutor-subtitle {
          font-family: "FK Grotesk", sans-serif;
          font-size: clamp(1.1rem, 2.5vw, 1.4rem);
          color: #8a8e9c;
          margin-bottom: 50px;
          font-weight: 400;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
          text-align: center;
          line-height: 1.6;
        }
        .tutor-cta {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 20px 44px;
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          border: none;
          border-radius: 50px;
          font-family: "FK Grotesk", sans-serif;
          font-weight: 600;
          font-size: 20px;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 10px 30px rgba(79, 140, 255, 0.18);
        }
        .tutor-cta:hover {
          transform: translateY(-3px);
          box-shadow: 0 20px 40px rgba(79, 140, 255, 0.22);
        }
        .tutor-platform-header {
          background: rgba(250, 250, 247, 0.98);
          border-bottom: 1px solid rgba(0, 0, 0, 0.07);
          padding: 24px 0 16px 0;
        }
        .tutor-back-btn {
          color: #8a8e9c;
          font-size: 15px;
          font-family: "FK Grotesk", sans-serif;
        }
        .tutor-back-btn:hover {
          color: #23273a;
        }
        .tutor-progress-container {
          margin-top: 10px;
        }
        .tutor-progress-bar {
          background: rgba(0, 0, 0, 0.08);
        }
        .tutor-progress-fill {
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          box-shadow: 0 0 10px rgba(79, 140, 255, 0.18);
        }
        .tutor-progress-percentage {
          color: #a259ff;
          font-weight: 700;
          font-family: "FK Grotesk", sans-serif;
        }
        .tutor-form-container {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px 40px; // 120px 20px 40px
        }
        .tutor-form-content {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .platform-step,
        .platform-completion-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          max-width: 600px;
          margin: 0 auto;
          padding: 40px 0 20px 0;
        }
        .platform-step-header {
          width: 100%;
          margin-bottom: 30px;
          text-align: center;
        }
        .platform-step-number {
          color: #a259ff;
          font-size: 1.2rem;
          font-weight: 600;
          display: block;
          margin-bottom: 10px;
        }
        .platform-step-title {
          font-family: "PP Editorial", serif;
          font-size: 2.2rem;
          font-weight: 400;
          color: #23273a;
          margin-bottom: 0;
        }
        .platform-step-subtitle {
          font-size: 1.2rem;
          color: #8a8e9c;
          margin-bottom: 30px;
          text-align: center;
          line-height: 1.6;
        }
        .platform-step-instruction {
          color: #8a8e9c;
          font-size: 1.1rem;
          margin-bottom: 40px;
          text-align: center;
        }
        .platform-select {
          width: 100%;
          padding: 15px;
          background: rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: 10px;
          color: #23273a;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .platform-select:focus {
          outline: none;
          border-color: #4f8cff;
          background: rgba(255, 255, 255, 0.9);
          transform: translateY(-2px);
        }
        .platform-options-grid {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .platform-option-btn {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 20px 25px;
          background: rgba(255, 255, 255, 0.6);
          border: 2px solid rgba(0, 0, 0, 0.1);
          border-radius: 12px;
          color: #23273a;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          text-align: left;
        }
        .platform-option-btn:hover {
          background: rgba(255, 255, 255, 0.8);
          border-color: rgba(79, 140, 255, 0.3);
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }
        .platform-option-btn.selected {
          background: rgba(79, 140, 255, 0.15);
          border-color: #4f8cff;
          transform: translateY(-2px);
        }
        .platform-option-letter {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.9rem;
          flex-shrink: 0;
          transition: all 0.3s ease;
        }
        .platform-option-btn.selected .platform-option-letter {
          background: #4f8cff;
          color: #fff;
        }
        .tutor-form-navigation {
          display: flex;
          gap: 24px;
          margin-top: 40px;
          width: 100%;
          max-width: 600px;
          justify-content: center;
        }
        .tutor-nav-btn-prev {
          background: transparent;
          color: #8a8e9c;
          border: 1.5px solid rgba(0, 0, 0, 0.18);
          font-family: "FK Grotesk", sans-serif;
        }
        .tutor-nav-btn-prev:hover {
          color: #23273a;
          border-color: #a259ff;
          transform: translateY(-2px);
        }
        .tutor-nav-btn-next {
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          font-family: "FK Grotesk", sans-serif;
          margin-left: auto;
          border: none;
        }
        .tutor-nav-btn-next:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(162, 89, 255, 0.13);
        }
        .platform-nav-hint {
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.85rem;
        }
        .platform-completion-step {
          text-align: center;
          max-width: 500px;
        }
        .platform-success-icon {
          margin-bottom: 30px;
          animation: successPulse 1s ease;
        }
        .platform-success-icon svg {
          color: #30d158;
        }
        .platform-submission-note {
          color: #8a8e9c;
          font-size: 0.9rem;
          margin-bottom: 30px;
          font-style: italic;
        }
        .platform-continue-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 18px 36px;
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          border: none;
          border-radius: 50px;
          font-weight: 600;
          font-size: 18px;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .platform-continue-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 20px 40px rgba(79, 140, 255, 0.4);
        }
        @keyframes successPulse {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        /* Floating Glowing Progress Bar */
        .tutor-floating-progress-bar {
          position: fixed;
          top: 32px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 1200;
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 420px;
          max-width: 90vw;
          pointer-events: none;
        }
        .tutor-progress-bar-outer {
          width: 100%;
          height: 14px;
          background: rgba(200, 200, 255, 0.13);
          border-radius: 12px;
          box-shadow: 0 0 32px 0 #a259ff44, 0 2px 12px #4f8cff22;
          overflow: hidden;
          margin-bottom: 6px;
          position: relative;
        }
        .tutor-progress-bar-inner {
          height: 100%;
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          border-radius: 12px;
          box-shadow: 0 0 18px 2px #a259ff88, 0 0 8px #4f8cff66;
          transition: width 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .tutor-progress-bar-label {
          color: #a259ff;
          font-family: "FK Grotesk", sans-serif;
          font-weight: 700;
          font-size: 1.1rem;
          text-shadow: 0 2px 8px #fff, 0 0 8px #a259ff33;
          margin-top: 0px;
          letter-spacing: 0.02em;
        }
        @media (max-width: 600px) {
          .tutor-floating-progress-bar {
            top: 16px;
            width: 98vw;
            max-width: 98vw;
          }
        }

        /* Beautiful Back Home Button */
        .tutor-back-home-btn {
          position: fixed;
          top: 32px;
          left: 32px;
          z-index: 1201;
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          border: none;
          border-radius: 32px;
          font-family: "FK Grotesk", sans-serif;
          font-weight: 600;
          font-size: 1.08rem;
          padding: 13px 32px 13px 22px;
          box-shadow: 0 4px 24px #a259ff33, 0 1.5px 6px #4f8cff22;
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
          outline: none;
        }
        .tutor-back-home-btn:hover,
        .tutor-back-home-btn:focus {
          background: linear-gradient(90deg, #a259ff 0%, #4f8cff 100%);
          box-shadow: 0 8px 32px #a259ff55, 0 2px 12px #4f8cff33;
          transform: translateY(-2px) scale(1.04);
        }
        .tutor-back-home-icon {
          font-size: 1.2em;
          margin-right: 6px;
          filter: drop-shadow(0 2px 8px #fff) drop-shadow(0 0 8px #a259ff33);
        }
        @media (max-width: 600px) {
          .tutor-back-home-btn {
            top: 12px;
            left: 8px;
            padding: 10px 18px 10px 14px;
            font-size: 0.98rem;
          }
        }
        .tutor-chat-question {
          color: #54555aff;
          text-align: center;
          font-size: 1.12rem;
          margin-bottom: 18px;
        }
        .tutor-chat-instruction {
          color: #8a8e9c;
          text-align: center;
          font-size: 1.08rem;
          margin-bottom: 18px;
        }
        .tutor-subjects-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px 18px;
          margin-bottom: 8px;
        }
        .tutor-subject-btn {
          min-width: 0;
          width: 100%;
          max-width: 240px;
          padding: 14px 12px;
          font-size: 1.01rem;
          border-radius: 14px;
          margin: 0 auto;
        }
        @media (max-width: 700px) {
          .tutor-subjects-grid {
            grid-template-columns: 1fr;
          }
          .tutor-subject-btn {
            max-width: 100%;
          }
        }
        .platform-option-icon svg {
          display: block;
          margin: 0 auto;
        }
        .tutor-form-action-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 16px 36px;
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          border: none;
          border-radius: 50px;
          font-family: "FK Grotesk", sans-serif;
          font-weight: 600;
          font-size: 1.08rem;
          box-shadow: 0 4px 24px #a259ff33, 0 1.5px 6px #4f8cff22;
          cursor: pointer;
          transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
          outline: none;
        }
        .tutor-form-action-btn:hover,
        .tutor-form-action-btn:focus {
          background: linear-gradient(90deg, #a259ff 0%, #4f8cff 100%);
          box-shadow: 0 8px 32px #a259ff55, 0 2px 12px #4f8cff33;
          transform: translateY(-2px) scale(1.04);
        }
        .tutor-form-action-btn.disabled,
        .tutor-form-action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          box-shadow: none;
          background: linear-gradient(90deg, #bdbdbd 0%, #e0e0e0 100%);
        }
        .tutor-form-action-icon {
          font-size: 1.2em;
          display: inline-flex;
          align-items: center;
          filter: drop-shadow(0 2px 8px #fff) drop-shadow(0 0 8px #a259ff33);
        }
        .tutor-form-action-back-icon {
          margin-right: 6px;
        }
        .tutor-form-action-next-icon {
          margin-left: 6px;
        }
      `}</style>
    </div>
  );
};

const ZentoraWebsite = () => {
  // Move all useState hooks to the top
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [showTutorPlatform, setShowTutorPlatform] = useState(false);
  const [showStudentPlatform, setShowStudentPlatform] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [studentCurrentStep, setStudentCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    timezone: "",
    subjectPreferences: [],
    teachingStyle: [],
    teachingPace: "",
    sessionFormat: [],
    experienceLevel: "",
    tutoringApproach: "",
    availabilityFlexibility: "",
    languagePreference: [],
    tutorBackground: [],
    helpFocus: "",
    idVerification: null,
    transcript: null,
    profilePhoto: null,
    introVideo: "",
  });
  const [studentFormData, setStudentFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    timezone: "",
    subjectNeeds: [],
    learningStyle: [],
    learningPace: "",
    sessionFormat: [],
    studentLevel: "",
    learningApproach: "",
    availabilityFlexibility: "",
    languagePreference: [],
    helpFocus: "",
  });
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("zentora-dark-mode") === "true";
    }
    return false;
  });
  const [showDashboardModal, setShowDashboardModal] = useState(false);

  // Parallax effect for hero SVGs
  const heroRef = useRef();
  // Only run parallax/scroll effects when not in platform views
  useEffect(() => {
    if (showTutorPlatform || showStudentPlatform) return;
    const handleScroll = () => {
      const y = window.scrollY;
      const hero = heroRef.current;
      if (hero) {
        hero.style.setProperty("--parallax", `${y * 0.15}px`);
      }
      // Parallax for blobs
      document.querySelectorAll(".section-visual-blob").forEach((el, i) => {
        el.style.transform = `translateY(${y * (0.06 + i * 0.01)}px)`;
      });
      // Parallax for iridescent lines
      document.querySelectorAll(".iridescent-line").forEach((el, i) => {
        el.style.transform = `translateY(${y * (0.03 + i * 0.01)}px)`;
      });
      // Parallax for animated gradient
      document.querySelectorAll(".animated-gradient-bg").forEach((el) => {
        el.style.transform = `translateY(${y * 0.04}px)`;
      });
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [showTutorPlatform, showStudentPlatform]);

  // Section Reveal Animation Hook
  function useSectionReveal(showTutorPlatform, showStudentPlatform) {
    const ref = useRef();
    useEffect(() => {
      const el = ref.current;
      if (!el) return;
      // Remove .visible when leaving homepage
      if (showTutorPlatform || showStudentPlatform) {
        el.classList.remove("visible");
        return;
      }
      const observer = new window.IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            el.classList.add("visible");
            observer.disconnect();
          }
        },
        { threshold: 0.18 }
      );
      observer.observe(el);
      return () => observer.disconnect();
    }, [showTutorPlatform, showStudentPlatform]);
    return ref;
  }

  const aboutRef = useSectionReveal(showTutorPlatform, showStudentPlatform);
  const audienceRef = useSectionReveal(showTutorPlatform, showStudentPlatform);
  const academicRef = useSectionReveal(showTutorPlatform, showStudentPlatform);
  const contactRef = useSectionReveal(showTutorPlatform, showStudentPlatform);
  const contactCardRefs = useStaggeredAnimateOnVisible(
    2,
    "zentora-contact-card",
    showTutorPlatform,
    showStudentPlatform
  );
  const featureRefs = useStaggeredAnimateOnVisible(
    features.length,
    "feature-card-animate",
    showTutorPlatform,
    showStudentPlatform
  );

  // Scroll to next section
  const scrollToAbout = () => {
    const about = document.getElementById("about");
    if (about) about.scrollIntoView({ behavior: "smooth" });
  };

  const [activeAudience, setActiveAudience] = useState("students");
  const audienceData = {
    students: {
      title: "For Students",
      description:
        "Find tutors who truly understand your learning style. Our platform doesn't just match you by subject—it considers your personality, communication preferences, and learning goals to create meaningful connections that accelerate your academic success.",
      benefits: [
        { text: "Faster, customized concept mastery" },
        { text: "Compatible learning partners" },
        { text: "Improved academic outcomes" },
      ],
    },
    parents: {
      title: "For Parents",
      description:
        "Peace of mind through transparency and results. Track your child's progress with detailed insights, see measurable learning outcomes, and trust that our smart matching ensures they're paired with high caliber tutors who bring out their best.",
      benefits: [
        { text: "Real-time progress tracking" },
        { text: "Verified, quality tutors" },
        { text: "Actionable learning insights" },
      ],
    },
    tutors: {
      title: "For Tutors",
      description:
        "Zentora connects top UIUC students with high-fit learners through precision matching. Tutors earn $30 per hour, choose their own schedule, and work with students aligned by subject, communication style, and learning goals. No cold outreach, no manual booking. Just focused, well-matched sessions.",
      benefits: [
        { text: "Earn anywhere from $30 - $45 per hour" },
        { text: "Higher session satisfaction" },
        { text: "Streamlined scheduling & payments" },
      ],
    },
  };

  const aiHeaderRef = useAnimateOnVisible(showTutorPlatform, showStudentPlatform);

  const heroHeaderRef = useRef();
  useEffect(() => {
    // Only animate hero when both platforms are hidden (homepage visible)
    if (!showTutorPlatform && !showStudentPlatform) {
      setTimeout(() => {
        if (heroHeaderRef.current) {
          heroHeaderRef.current.classList.add("visible");
        }
      }, 120);
    } else {
      // Remove the visible class so it can re-animate next time
      if (heroHeaderRef.current) {
        heroHeaderRef.current.classList.remove("visible");
      }
    }
  }, [showTutorPlatform, showStudentPlatform]);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("dark-mode");
    } else {
      document.body.classList.remove("dark-mode");
    }
    localStorage.setItem("zentora-dark-mode", darkMode);
  }, [darkMode]);

  // NEW: Event handlers
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === "tutor") {
      setShowPasswordModal(false);
      setShowTutorPlatform(true);
      setPassword("");
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
  };

  const handleFormUpdate = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleStudentFormUpdate = (field, value) => {
    setStudentFormData((prev) => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const studentNextStep = () => {
    setStudentCurrentStep((prev) => prev + 1);
  };

  const studentPrevStep = () => {
    setStudentCurrentStep((prev) => (prev > 0 ? prev - 1 : 0));
  };

  // Reset platform state when returning home
  useEffect(() => {
    if (!showTutorPlatform) {
      setCurrentStep(0);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        timezone: "",
        subjectPreferences: [],
        teachingStyle: [],
        teachingPace: "",
        sessionFormat: [],
        experienceLevel: "",
        tutoringApproach: "",
        availabilityFlexibility: "",
        languagePreference: [],
        tutorBackground: [],
        helpFocus: "",
        idVerification: null,
        transcript: null,
        profilePhoto: null,
        introVideo: "",
      });
    }
    if (!showStudentPlatform) {
      setStudentCurrentStep(0);
      setStudentFormData({
        firstName: "",
        lastName: "",
        email: "",
        timezone: "",
        subjectNeeds: [],
        learningStyle: [],
        learningPace: "",
        sessionFormat: [],
        studentLevel: "",
        learningApproach: "",
        availabilityFlexibility: "",
        languagePreference: [],
        helpFocus: "",
      });
    }
  }, [showTutorPlatform, showStudentPlatform]);

  // Secret dashboard modal keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      if ((isMac ? e.metaKey : e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "d") {
        e.preventDefault();
        setShowDashboardModal(true);
      }
      if (e.key === "Escape") {
        setShowDashboardModal(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // NEW: If showing platforms, render them instead of main website
  if (showTutorPlatform) {
    return (
      <>
        <TutorPlatform
          currentStep={currentStep}
          formData={formData}
          handleFormUpdate={handleFormUpdate}
          nextStep={nextStep}
          prevStep={prevStep}
          setShowTutorPlatform={setShowTutorPlatform}
          setCurrentStep={setCurrentStep}
        />
        {showDashboardModal && (
          <div className="dashboard-modal-overlay" onClick={() => setShowDashboardModal(false)}>
            <div className="dashboard-modal" onClick={(e) => e.stopPropagation()}>
              <h2 className="dashboard-modal-title">Secret Dashboards</h2>
              <button
                className="dashboard-modal-btn"
                onClick={() => {
                  setShowDashboardModal(false);
                  window.location.href = "/tutor-dashboard";
                }}>
                Tutor Dashboard
              </button>
              <button
                className="dashboard-modal-btn"
                onClick={() => {
                  setShowDashboardModal(false);
                  window.location.href = "/executive-dashboard";
                }}>
                Executive Dashboard
              </button>
              <p className="dashboard-modal-hint">(Press Esc or click outside to close)</p>
            </div>
          </div>
        )}
      </>
    );
  }

  if (showStudentPlatform) {
    return (
      <>
        <StudentPlatform
          currentStep={studentCurrentStep}
          formData={studentFormData}
          handleFormUpdate={handleStudentFormUpdate}
          nextStep={studentNextStep}
          prevStep={studentPrevStep}
          setShowStudentPlatform={setShowStudentPlatform}
          setCurrentStep={setStudentCurrentStep}
        />
        {showDashboardModal && (
          <div className="dashboard-modal-overlay" onClick={() => setShowDashboardModal(false)}>
            <div className="dashboard-modal" onClick={(e) => e.stopPropagation()}>
              <h2 className="dashboard-modal-title">Secret Dashboards</h2>
              <button
                className="dashboard-modal-btn"
                onClick={() => {
                  setShowDashboardModal(false);
                  window.location.href = "/tutor-dashboard";
                }}>
                Tutor Dashboard
              </button>
              <button
                className="dashboard-modal-btn"
                onClick={() => {
                  setShowDashboardModal(false);
                  window.location.href = "/executive-dashboard";
                }}>
                Executive Dashboard
              </button>
              <p className="dashboard-modal-hint">(Press Esc or click outside to close)</p>
            </div>
          </div>
        )}
      </>
    );
  }

  // Dashboard route logic
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  if (path === "/tutor-dashboard") {
    return <TutorDashboard />;
  }
  if (path === "/executive-dashboard") {
    return <ExecutiveDashboard />;
  }

  return (
    <div className="zentora-light">
      {/* Logo Corner Button */}
      <div
        className="zentora-logo-corner"
        onClick={(e) => {
          console.log("Logo clicked!"); // Debug log
          e.preventDefault();
          e.stopPropagation();
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth",
          });
        }}
        onDoubleClick={() => setDarkMode((dm) => !dm)}
        role="button"
        tabIndex={0}
        aria-label="Go to homepage"
        style={{ cursor: "pointer" }}>
        <img
          src="/zentora-transparent-white.png"
          alt="Zentora Logo"
          style={{
            width: 44,
            height: 44,
            objectFit: "contain",
            pointerEvents: "none",
          }}
        />
      </div>
      <nav className="zentora-nav reinvented-nav">
        <div className="zentora-nav-buttons nav-buttons-float">
          <button
            className="zentora-nav-btn"
            style={{ cursor: "pointer" }}
            onClick={() => {
              const el = document.getElementById("about");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}>
            About
          </button>
          <button
            className="zentora-nav-btn"
            style={{ cursor: "pointer" }}
            onClick={() => {
              const el = document.getElementById("audience");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}>
            For Everyone
          </button>
          <button
            className="zentora-nav-btn"
            style={{ cursor: "pointer" }}
            onClick={() => {
              const el = document.getElementById("academic-intelligence");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}>
            Academic Intelligence
          </button>
          <button
            className="zentora-nav-btn"
            style={{ cursor: "pointer" }}
            onClick={() => {
              const el = document.getElementById("contact");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}>
            Contact
          </button>
          {/* NEW: Tutor Platform button */}
          <button className="zentora-nav-btn tutor-platform-btn" onClick={() => setShowPasswordModal(true)}>
            Tutor Onboarding
          </button>
        </div>
      </nav>

      {/* NEW: Password Modal */}
      {showPasswordModal && (
        <div className="password-modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="password-modal glass-modal" onClick={(e) => e.stopPropagation()}>
            {/* Matrix/Secret Code Effect */}
            <div className="matrix-bg">
              <div className="matrix-rain"></div>
            </div>
            {/* Animated Logo */}
            <div className="zentora-modal-logo">
              <img src="/zentora-transparent-white.png" alt="Zentora Logo" />
            </div>
            <button className="modal-close" onClick={() => setShowPasswordModal(false)} aria-label="Close">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div className="modal-title-row">
              <span className="modal-lock-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#a259ff" strokeWidth="2.2">
                  <rect x="5" y="11" width="14" height="8" rx="3" fill="#f3f3f7" />
                  <path d="M8 11V8a4 4 0 1 1 8 0v3" stroke="#a259ff" strokeWidth="2.2" fill="none" />
                </svg>
              </span>
              <h2 className="modal-title pp-editorial-italic">Tutor Platform Access</h2>
            </div>
            <p className="modal-subtitle">Enter the secret code to unlock access</p>
            <form onSubmit={handlePasswordSubmit}>
              <input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError(false);
                }}
                className={`modal-input ${passwordError ? "error" : ""}`}
                autoFocus
              />
              {passwordError && <span className="error-message">Incorrect password. Please try again.</span>}
              <button type="submit" className="submit-btn modal-submit-btn">
                Access Platform
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="zentora-hero fade-in" id="home" ref={heroRef}>
        <SectionVisuals
          arcColor="#a259ff"
          blobColor="#4f8cff"
          arcStyle={{ top: "8%" }}
          blobStyle={{ top: "30%", left: "70vw", size: 180 }}
        />
        <AnimatedGradient />
        <div className="zentora-hero-content">
          <h1 ref={heroHeaderRef} className="zentora-hero-title header-float-up">
            Elite Peer T<span className="pp-editorial-italic">u</span>toring,
            <br />
            <span className="gradient-text">Reimagined.</span>
          </h1>
          <div className="zentora-hero-sub">
            Where most tutoring platforms stop at matching, we go further—building the system that matches right.
          </div>
          {/* MODIFIED: Student platform button */}
          <button className="zentora-cta" onClick={() => setShowStudentPlatform(true)}>
            Find a match!
          </button>
        </div>
        <FuturisticLines />
      </section>
      {/* About Section */}
      <section className="zentora-about fade-in-up section-reveal" id="about" ref={aboutRef}>
        <SectionVisuals
          arcColor="#43e6b0"
          blobColor="#ffe066"
          arcStyle={{ top: "60%" }}
          blobStyle={{ top: "70%", left: "10vw", size: 140 }}
        />
        <div className="zentora-about-grid">
          <div className="zentora-about-left glass-card">
            <h2 className="zentora-section-title">Smart Matching. Real Results.</h2>
            <div className="zentora-about-text">
              Zentora matches students and tutors by subject, communication style, and learning fit. We use session
              data, feedback, and performance signals to ensure every pairing leads to meaningful academic progress.
              <br />
              <br />
              The platform was built at the University of Illinois Urbana-Champaign and is supported by local engineers,
              educators, and students who understand what real learning support looks like. Zentora is designed for
              schools that need trusted results and for families who want tutors that make a measurable difference.
            </div>
          </div>
          <div className="zentora-about-features">
            <div className="zentora-features-grid fade-in-up">
              {features.map((f, i) => (
                <div
                  className="zentora-feature-card glass-card feature-card-animate"
                  key={i}
                  data-index={i}
                  ref={(el) => (featureRefs.current[i] = el)}>
                  <div className="zentora-feature-title">{f.title}</div>
                  <div className="zentora-feature-desc">{f.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <FuturisticLines />
      </section>
      {/* Audience Section */}
      <section className="zentora-audience fade-in-up section-reveal" id="audience" ref={audienceRef}>
        <SectionVisuals
          arcColor="#ff6bcb"
          blobColor="#a259ff"
          arcStyle={{ top: "20%" }}
          blobStyle={{ top: "60%", left: "80vw", size: 120 }}
        />
        <h2 className="zentora-section-title gradient-text">
          Built <span className="italic">for</span>{" "}
          <TypewriterWord words={["Everyone", "Students", "Parents", "Tutors"]} />
        </h2>
        <div className="zentora-audience-cards">
          <div className="zentora-audience-card">
            <div className="zentora-audience-tabs centered-tabs">
              <button
                className={activeAudience === "students" ? "active" : ""}
                onClick={() => setActiveAudience("students")}>
                Students
              </button>
              <button
                className={activeAudience === "parents" ? "active" : ""}
                onClick={() => setActiveAudience("parents")}>
                Parents
              </button>
              <button
                className={activeAudience === "tutors" ? "active" : ""}
                onClick={() => setActiveAudience("tutors")}>
                Tutors
              </button>
            </div>
            <div className="zentora-audience-content">
              <div className="zentora-audience-title">{audienceData[activeAudience].title}</div>
              <div className="zentora-audience-desc">{audienceData[activeAudience].description}</div>
              <ul className="zentora-audience-benefits">
                {audienceData[activeAudience].benefits.map((b, i) => (
                  <li key={i}>{b.text}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
      {/* Academic Intelligence Section */}
      <section className="zentora-academic fade-in-up section-reveal" id="academic-intelligence" ref={academicRef}>
        <SectionVisuals
          arcColor="#ffe066"
          blobColor="#43e6b0"
          arcStyle={{ top: "10%" }}
          blobStyle={{ top: "50%", left: "20vw", size: 160 }}
        />
        <div className="zentora-academic-grid">
          <div className="zentora-academic-visual">
            <AnimatedMatchingSVG />
          </div>
          <div className="zentora-academic-content">
            <h2 ref={aiHeaderRef} className="zentora-section-title gradient-text ai-header-animate">
              Introducing Academic Intelligence
            </h2>
            <div className="zentora-academic-sub">
              Our proprietary ML/heuristic system analyzes multiple dimensions to create connections that work.
            </div>
            <div className="zentora-academic-features">
              <div className="zentora-academic-feature glass-card">
                <div className="zentora-academic-feature-title">Multi-Dimensional Analysis</div>
                <div className="zentora-academic-feature-desc">
                  Analyzing subjects, learning styles, personality compatibility, communication preferences, and
                  historical session outcomes to find the perfect tutor-student pairs.
                </div>
              </div>
              <div className="zentora-academic-feature glass-card">
                <div className="zentora-academic-feature-title">Continuous Learning</div>
                <div className="zentora-academic-feature-desc">
                  Our system learns from every session, review, and outcome—constantly improving match quality and
                  predicting which partnerships will succeed.
                </div>
              </div>
              <div className="zentora-academic-feature glass-card">
                <div className="zentora-academic-feature-title">Outcome Prediction</div>
                <div className="zentora-academic-feature-desc">
                  Beyond just matching, we predict learning outcomes and session satisfaction, ensuring both students
                  and tutors have the best possible experience.
                </div>
              </div>
              <div className="zentora-academic-feature glass-card">
                <div className="zentora-academic-feature-title">Real-Time Optimization</div>
                <div className="zentora-academic-feature-desc">
                  Smart heuristics adjust recommendations in real-time based on availability, performance trends, and
                  emerging learning patterns across our platform.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Contact Section */}
      <section className="zentora-contact section-reveal" id="contact" ref={contactRef}>
        <SectionVisuals
          arcColor="#4f8cff"
          blobColor="#ff6bcb"
          arcStyle={{ top: "80%" }}
          blobStyle={{ top: "80%", left: "60vw", size: 100 }}
        />
        <h2 className="zentora-section-title gradient-text">
          Ready to <span className="italic">Transform</span> Learning?
        </h2>
        <div className="zentora-contact-cards">
          <div className="zentora-contact-card" data-index="0" ref={(el) => (contactCardRefs.current[0] = el)}>
            <div className="zentora-contact-title">Join Our Team</div>
            <div className="zentora-contact-desc">Check out our LinkedIn for open part-time and full-time roles.</div>
            <a href="https://www.linkedin.com/company/zentoraedu" target="_blank" rel="noopener noreferrer">
              <button className="zentora-cta secondary">LinkedIn</button>
            </a>
          </div>
          <div className="zentora-contact-card" data-index="1" ref={(el) => (contactCardRefs.current[1] = el)}>
            <div className="zentora-contact-title">Partner With Us</div>
            <div className="zentora-contact-desc">Interested in bringing Zentora to your institution? Let's talk.</div>
            <a href="mailto:ops@zentora.biz">
              <button className="zentora-cta secondary">Get in Touch</button>
            </a>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="zentora-footer">
        <div className="zentora-footer-content">
          <span className="zentora-footer-copyright">© 2025 Zentora. All rights reserved.</span>
          <span className="zentora-footer-tagline">Building the future of peer education</span>
        </div>
      </footer>
      {showDashboardModal && (
        <div className="dashboard-modal-overlay" onClick={() => setShowDashboardModal(false)}>
          <div className="dashboard-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="dashboard-modal-title">Secret Dashboards</h2>
            <button
              className="dashboard-modal-btn"
              onClick={() => {
                setShowDashboardModal(false);
                window.location.href = "/tutor-dashboard";
              }}>
              Tutor Dashboard
            </button>
            <button
              className="dashboard-modal-btn"
              onClick={() => {
                setShowDashboardModal(false);
                window.location.href = "/executive-dashboard";
              }}>
              Executive Dashboard
            </button>
            <p className="dashboard-modal-hint">(Press Esc or click outside to close)</p>
          </div>
        </div>
      )}
      <style jsx>{`
        body,
        html,
        .zentora-light {
          background: #fafaf7;
          min-height: 100vh;
          font-family: "FK Grotesk", sans-serif;
          color: #23273a;
          overflow-x: hidden;
        }
        body {
          overflow-y: scroll;
        }
        .zentora-nav {
          position: fixed;
          top: 24px;
          right: 24px;
          z-index: 1000;
          pointer-events: auto;
          overflow: visible !important;
          min-height: 56px;
          height: 56px;
        }
        .zentora-nav-buttons {
          display: flex;
          gap: 18px;
          padding: 0;
          background: none;
          box-shadow: none;
          border-radius: 0;
          backdrop-filter: none;
          overflow: visible !important;
          min-height: 56px;
          height: 56px;
        }
        .zentora-nav-btn {
          background: #fff;
          border: none;
          border-radius: 22px;
          padding: 14px 28px;
          font-size: 16px;
          font-weight: 500;
          color: #23273a;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          white-space: nowrap;
          box-shadow: 0 0 12px 0 rgba(162, 89, 255, 0.06);
          margin: 0;
          position: relative;
          z-index: 1;
          outline-offset: -2px;
        }
        .zentora-nav-btn:hover,
        .zentora-nav-btn:focus {
          background: #f3f3f7;
          color: #23273a;
          transform: scale(1.08);
          box-shadow: 0 0 48px 0 rgba(120, 100, 255, 0.18);
          outline: none;
        }
        .zentora-nav-btn:not(:last-child) {
          margin-right: 0px;
        }
        .zentora-nav-btn:hover,
        .zentora-nav-btn:focus {
          background: #f3f3f7;
          color: #23273a;
          transform: scale(1.08);
          box-shadow: 0 0 48px 0 rgba(120, 100, 255, 0.18);
          outline: none;
        }
        .tutor-platform-btn {
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          font-weight: 600;
          box-shadow: 0 4px 18px #a259ff22;
        }
        .tutor-platform-btn:hover {
          background: linear-gradient(90deg, #a259ff 0%, #4f8cff 100%);
          color: #fff;
        }
        @media (max-width: 900px) {
          .zentora-nav-buttons {
            gap: 8px;
          }
          .zentora-nav-btn {
            padding: 10px 16px;
            font-size: 13px;
          }
        }
        @media (max-width: 600px) {
          .zentora-nav-buttons {
            flex-direction: column;
            gap: 4px;
          }
          .zentora-nav-btn {
            padding: 10px 16px;
            font-size: 12px;
          }
        }

        /* Scroll offset for sections to account for fixed navigation */
        #about,
        #audience,
        #academic-intelligence,
        #contact {
          scroll-margin-top: 80px;
        }

        /* Password Modal */
        .password-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: fadeIn 0.3s ease;
        }
        .password-modal {
          background: rgba(255, 255, 255, 0.95);
          border-radius: 20px;
          padding: 40px;
          max-width: 400px;
          width: 90%;
          position: relative;
          animation: slideUp 0.3s ease;
          backdrop-filter: blur(20px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          text-align: center;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .modal-close {
          position: absolute;
          top: 20px;
          right: 20px;
          background: none;
          border: none;
          color: #666;
          cursor: pointer;
          padding: 5px;
          transition: all 0.3s ease;
        }
        .modal-close:hover {
          color: #23273a;
        }
        .password-modal h2 {
          color: #23273a;
          font-size: 1.8rem;
          margin-bottom: 10px;
          font-weight: 600;
          text-align: center;
        }
        .password-modal p {
          color: #8a8e9c;
          margin-bottom: 30px;
          font-size: 1rem;
          text-align: center;
        }
        .password-modal form {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
        }
        .password-modal input {
          width: 100%;
          max-width: 300px;
          padding: 15px;
          background: rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: 10px;
          color: #23273a;
          font-size: 16px;
          margin-bottom: 20px;
          transition: all 0.3s ease;
          text-align: center;
        }
        .password-modal input:focus {
          outline: none;
          border-color: #4f8cff;
          background: rgba(0, 0, 0, 0.08);
        }
        .password-modal input.error {
          border-color: #ff3b30;
        }
        .error-message {
          color: #ff3b30;
          font-size: 0.85rem;
          display: block;
          margin-top: -15px;
          margin-bottom: 15px;
          text-align: center;
        }
        .submit-btn {
          width: 100%;
          max-width: 300px;
          padding: 15px;
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(79, 140, 255, 0.3);
        }

        /* Platform Styles */
        .zentora-platform {
          min-height: 100vh;
          background: #fafaf7;
          color: #23273a;
          position: relative;
          overflow-x: hidden;
        }
        .platform-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: rgba(250, 250, 247, 0.95);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
          padding: 20px;
          z-index: 100;
        }
        .platform-back-btn {
          background: none;
          border: none;
          color: #8a8e9c;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.3s ease;
          padding: 5px 0;
        }
        .platform-back-btn:hover {
          color: #23273a;
        }
        .platform-progress-container {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-top: 10px;
        }
        .platform-progress-bar {
          flex: 1;
          height: 4px;
          background: rgba(0, 0, 0, 0.1);
          border-radius: 2px;
          overflow: hidden;
        }
        .platform-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          transition: width 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          box-shadow: 0 0 10px rgba(79, 140, 255, 0.5);
        }
        .platform-progress-percentage {
          color: #4f8cff;
          font-weight: 600;
          font-size: 14px;
          min-width: 40px;
          text-align: right;
        }
        .platform-form-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          padding: 100px 20px 40px;
          max-width: 800px;
          margin: 0 auto;
        }
        .platform-form-content {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          opacity: 0;
          transform: translateY(30px);
          animation: stepFadeIn 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s forwards;
        }
        @keyframes stepFadeIn {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Platform Welcome Step */
        .platform-welcome-step {
          text-align: center;
          max-width: 700px;
          position: relative;
          overflow: hidden;
          height: 70vh;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }
        .platform-welcome-title {
          font-size: clamp(3rem, 6vw, 5rem);
          font-weight: 900;
          color: #23273a;
          margin-bottom: 30px;
          letter-spacing: -0.05em;
          line-height: 1.05;
          animation: welcomeFadeInUp 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s both;
          text-rendering: optimizeLegibility;
        }
        .platform-gradient-text {
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: welcomeGradientAnimation 3s ease-in-out infinite;
        }
        @keyframes welcomeGradientAnimation {
          0%,
          100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        .platform-welcome-subtitle {
          font-size: clamp(1.1rem, 2.5vw, 1.4rem);
          color: #8a8e9c;
          margin-bottom: 50px;
          font-weight: 400;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
          animation: welcomeFadeInUp 1s ease-out 0.8s both;
          line-height: 1.6;
        }
        .platform-welcome-cta {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 20px 40px;
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          border: none;
          border-radius: 50px;
          font-weight: 600;
          font-size: 18px;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          animation: welcomeFadeInUp 1s ease-out 1.3s both;
          box-shadow: 0 10px 30px rgba(79, 140, 255, 0.3);
        }
        .platform-welcome-cta:hover {
          transform: translateY(-3px);
          box-shadow: 0 20px 40px rgba(79, 140, 255, 0.4);
        }
        .platform-btn-arrow {
          transition: transform 0.3s ease;
          font-size: 20px;
        }
        .platform-welcome-cta:hover .platform-btn-arrow {
          transform: translateX(5px);
        }
        @keyframes welcomeFadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Platform Step Styles */
        .platform-step {
          width: 100%;
          max-width: 600px;
        }
        .platform-step-header {
          width: 100%;
          margin-bottom: 30px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .platform-step-number {
          color: #a259ff;
          font-size: 1.2rem;
          font-weight: 600;
          display: block;
          margin-bottom: 10px;
        }
        .platform-step-title {
          font-family: "PP Editorial", serif;
          font-size: 2.2rem;
          font-weight: 400;
          color: #23273a;
          margin-bottom: 0;
        }
        .platform-step-subtitle {
          font-size: 1.2rem;
          color: #8a8e9c;
          margin-bottom: 30px;
          text-align: center;
          line-height: 1.6;
        }
        .platform-step-instruction {
          color: #8a8e9c;
          font-size: 1.1rem;
          margin-bottom: 40px;
          text-align: center;
        }
        .platform-select {
          width: 100%;
          padding: 15px;
          background: rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: 10px;
          color: #23273a;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .platform-select:focus {
          outline: none;
          border-color: #4f8cff;
          background: rgba(255, 255, 255, 0.9);
          transform: translateY(-2px);
        }
        .platform-options-grid {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .platform-option-btn {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 20px 25px;
          background: rgba(255, 255, 255, 0.6);
          border: 2px solid rgba(0, 0, 0, 0.1);
          border-radius: 12px;
          color: #23273a;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          text-align: left;
        }
        .platform-option-btn:hover {
          background: rgba(255, 255, 255, 0.8);
          border-color: rgba(79, 140, 255, 0.3);
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }
        .platform-option-btn.selected {
          background: rgba(79, 140, 255, 0.15);
          border-color: #4f8cff;
          transform: translateY(-2px);
        }
        .platform-option-letter {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.9rem;
          flex-shrink: 0;
          transition: all 0.3s ease;
        }
        .platform-option-btn.selected .platform-option-letter {
          background: #4f8cff;
          color: #fff;
        }
        .tutor-form-navigation {
          display: flex;
          gap: 24px;
          margin-top: 40px;
          width: 100%;
          max-width: 600px;
          justify-content: center;
        }
        .tutor-nav-btn-prev {
          background: transparent;
          color: #8a8e9c;
          border: 1.5px solid rgba(0, 0, 0, 0.18);
          font-family: "FK Grotesk", sans-serif;
        }
        .tutor-nav-btn-prev:hover {
          color: #23273a;
          border-color: #a259ff;
          transform: translateY(-2px);
        }
        .tutor-nav-btn-next {
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          font-family: "FK Grotesk", sans-serif;
          margin-left: auto;
          border: none;
        }
        .tutor-nav-btn-next:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(162, 89, 255, 0.13);
        }
        .platform-nav-hint {
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.85rem;
        }
        .platform-completion-step {
          text-align: center;
          max-width: 500px;
        }
        .platform-success-icon {
          margin-bottom: 30px;
          animation: successPulse 1s ease;
        }
        .platform-success-icon svg {
          color: #30d158;
        }
        .platform-submission-note {
          color: #8a8e9c;
          font-size: 0.9rem;
          margin-bottom: 30px;
          font-style: italic;
        }
        .platform-continue-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 18px 36px;
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          border: none;
          border-radius: 50px;
          font-weight: 600;
          font-size: 18px;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .platform-continue-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 20px 40px rgba(79, 140, 255, 0.4);
        }
        @keyframes successPulse {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .zentora-nav {
          position: fixed;
          top: 24px;
          left: 0;
          right: 0;
          z-index: 100;
          display: flex;
          justify-content: center;
          pointer-events: none;
        }
        .zentora-nav-inner {
          background: #fff;
          border-radius: 32px;
          box-shadow: 0 2px 16px rgba(0, 0, 0, 0.04);
          padding: 10px 36px;
          display: flex;
          align-items: center;
          gap: 32px;
          pointer-events: auto;
        }
        .zentora-logo {
          height: 36px;
          margin-right: 24px;
        }
        .zentora-nav-links {
          display: flex;
          gap: 18px;
          list-style: none;
        }
        .zentora-nav-links li a {
          display: block;
          padding: 8px 22px;
          border-radius: 22px;
          font-weight: 500;
          color: #23273a;
          text-decoration: none;
          transition: background 0.2s, color 0.2s;
        }
        .zentora-nav-links li a:hover {
          background: #f3f3f7;
          color: #4f8cff;
        }
        .zentora-hero {
          min-height: 90vh;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          background: #fafaf7;
          overflow: hidden;
        }
        .animated-gradient-bg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
          animation: gradientMove 12s ease-in-out infinite alternate;
        }
        .animated-gradient-bg .g1,
        .animated-gradient-bg .g2 {
          filter: blur(2px);
        }
        @keyframes gradientMove {
          0% {
            filter: hue-rotate(0deg) blur(2px);
          }
          100% {
            filter: hue-rotate(40deg) blur(2px);
          }
        }
        .zentora-hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 800px;
          margin: 0 auto;
          padding-top: 120px;
        }
        .zentora-hero-title {
          font-family: "PP Editorial", serif;
          font-size: clamp(2.8rem, 8vw, 5.2rem);
          font-weight: 400;
          color: #23273a;
          margin-bottom: 24px;
          letter-spacing: -0.03em;
        }
        .gradient-text {
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-family: "PP Editorial Italic", serif;
          font-style: italic;
        }
        .zentora-hero-sub {
          font-size: 1.3rem;
          color: #8a8e9c;
          margin-bottom: 38px;
          font-family: "FK Grotesk", sans-serif;
          font-weight: 400;
        }
        .zentora-cta {
          background: rgba(255, 255, 255, 0.18);
          color: #23273a;
          border: 1.5px solid #e0e0e0;
          border-radius: 32px;
          font-size: 1.2rem;
          font-weight: 600;
          padding: 18px 44px;
          box-shadow: 0 2px 16px rgba(79, 140, 255, 0.07);
          backdrop-filter: blur(8px);
          display: inline-flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          transition: background 0.2s, box-shadow 0.2s, transform 0.2s, border 0.2s;
        }
        .zentora-cta:hover {
          background: rgba(79, 140, 255, 0.09);
          color: #4f8cff;
          box-shadow: 0 4px 24px rgba(162, 89, 255, 0.1);
          border: 1.5px solid #a259ff;
          transform: translateY(-2px) scale(1.04);
        }
        .futuristic-lines {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          z-index: 1;
          pointer-events: none;
        }
        .zentora-features {
          background: none;
          padding: 80px 0 60px 0;
          display: flex;
          justify-content: center;
        }
        .zentora-features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 36px;
          max-width: 1100px;
          width: 100%;
        }
        .zentora-feature-card {
          background: rgba(255, 255, 255, 0.7);
          box-shadow: 0 4px 32px rgba(79, 140, 255, 0.07);
          border-radius: 24px;
          padding: 38px 30px 32px 30px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          min-height: 220px;
          border: 1.5px solid #f3f3f7;
          transition: box-shadow 0.2s, transform 0.2s;
          backdrop-filter: blur(8px);
        }
        .zentora-feature-card:hover {
          box-shadow: 0 12px 48px 0 rgba(79, 140, 255, 0.13), 0 1.5px 6px 0 rgba(162, 89, 255, 0.08);
          transform: translateY(-4px) scale(1.03);
        }
        .zentora-feature-icon {
          font-size: 2.1rem;
          margin-bottom: 18px;
        }
        .zentora-feature-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #23273a;
          margin-bottom: 10px;
        }
        .zentora-feature-desc {
          color: #7a7e8c;
          font-size: 1.08rem;
        }
        .zentora-section-title {
          font-family: "PP Editorial", serif;
          font-size: clamp(2.2rem, 5vw, 3.2rem);
          font-weight: 400;
          color: #23273a;
          margin-bottom: 18px;
          letter-spacing: -0.03em;
          text-align: center;
        }
        .zentora-section-title .italic {
          font-family: "PP Editorial Italic", serif;
          font-style: italic;
        }
        .gradient-text {
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .zentora-audience {
          background: none;
          padding: 120px 20px 100px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .zentora-audience-cards {
          display: flex;
          justify-content: center;
          width: 100%;
        }
        .zentora-audience-card {
          background: rgba(255, 255, 255, 0.7);
          box-shadow: 0 4px 32px rgba(79, 140, 255, 0.07);
          border-radius: 24px;
          padding: 38px 36px 32px 36px;
          min-width: 340px;
          max-width: 600px;
          width: 100%;
          border: 1.5px solid #f3f3f7;
          margin-top: 32px;
          backdrop-filter: blur(8px);
          min-height: 320px;
          height: auto;
          padding-bottom: 48px;
        }
        .zentora-audience-tabs {
          display: flex;
          gap: 12px;
          margin-bottom: 18px;
        }
        .zentora-audience-tabs button {
          background: #f3f3f7;
          border: none;
          border-radius: 18px;
          padding: 8px 22px;
          font-size: 1.05rem;
          color: #23273a;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s, color 0.2s;
        }
        .zentora-audience-tabs button.active,
        .zentora-audience-tabs button:hover {
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
        }
        .zentora-audience-title {
          font-size: 1.3rem;
          font-weight: 700;
          color: #23273a;
          margin-bottom: 10px;
        }
        .zentora-audience-desc {
          color: #7a7e8c;
          font-size: 1.08rem;
          margin-bottom: 18px;
        }
        .zentora-audience-benefits {
          color: #23273a;
          font-size: 1.08rem;
          margin: 0;
          padding-left: 18px;
        }
        .zentora-audience-benefits li {
          margin-bottom: 8px;
        }
        .zentora-contact {
          background: none;
          padding: 100px 20px 80px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .zentora-contact-cards {
          display: flex;
          gap: 32px;
          justify-content: center;
          margin-top: 32px;
          flex-wrap: wrap;
          margin-bottom: 40px;
        }
        .zentora-contact-card {
          background: rgba(255, 255, 255, 0.7);
          box-shadow: 0 8px 32px rgba(79, 140, 255, 0.08);
          border-radius: 24px;
          padding: 28px 24px;
          min-width: 240px;
          max-width: 280px;
          width: 100%;
          border: none;
          backdrop-filter: blur(12px);
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 160px;
          opacity: 0;
          transform: translateY(30px) scale(0.95);
          transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .zentora-contact-card:hover {
          box-shadow: 0 20px 60px rgba(162, 89, 255, 0.15);
          transform: translateY(-8px) scale(1.05);
          background: rgba(255, 255, 255, 0.85);
        }

        /* Contact card animation when visible */
        .zentora-contact-card.visible {
          opacity: 1;
          transform: translateY(0) scale(1);
          animation: contactFloat 6s ease-in-out infinite;
        }

        @keyframes contactFloat {
          0%,
          100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-5px) scale(1);
          }
        }

        /* Staggered animation for contact cards */
        .zentora-contact-card:nth-child(1) {
          transition-delay: 0.1s;
          animation-delay: 0s;
        }
        .zentora-contact-card:nth-child(2) {
          transition-delay: 0.3s;
          animation-delay: 0.5s;
        }
        .zentora-contact-title {
          font-size: 1.4rem;
          font-weight: 700;
          color: #23273a;
          margin-bottom: 12px;
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s;
        }
        .zentora-contact-card.visible .zentora-contact-title {
          opacity: 1;
          transform: translateY(0);
        }
        .zentora-contact-title .italic {
          font-family: "PP Editorial Italic", serif;
          font-style: italic;
        }
        .zentora-contact-desc {
          color: #6a6e7c;
          font-size: 1.05rem;
          margin-bottom: 24px;
          text-align: center;
          line-height: 1.6;
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.4s;
        }
        .zentora-contact-card.visible .zentora-contact-desc {
          opacity: 1;
          transform: translateY(0);
        }
        .zentora-cta.secondary {
          margin-top: 0;
          padding: 14px 28px;
          font-size: 15px;
          font-weight: 600;
          opacity: 0;
          transform: translateY(20px) scale(0.9);
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.6s;
        }
        .zentora-contact-card.visible .zentora-cta.secondary {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
        .zentora-about-content.glass-card {
          background: rgba(255, 255, 255, 0.7);
          box-shadow: 0 4px 32px rgba(79, 140, 255, 0.07);
          border-radius: 24px;
          padding: 38px 36px 32px 36px;
          min-width: 340px;
          max-width: 700px;
          width: 100%;
          border: 1.5px solid #f3f3f7;
          margin-top: 32px;
          backdrop-filter: blur(8px);
          transition: box-shadow 0.2s, transform 0.2s;
        }
        .zentora-about-content.glass-card:hover {
          box-shadow: 0 12px 48px 0 rgba(79, 140, 255, 0.13), 0 1.5px 6px 0 rgba(162, 89, 255, 0.08);
          transform: translateY(-4px) scale(1.03);
        }
        .zentora-about-text {
          color: #23273a;
          font-size: 1.13rem;
          margin-top: 12px;
          line-height: 1.7;
        }
        /* Animations */
        .fade-in {
          opacity: 0;
          animation: fadeIn 1.2s ease 0.2s forwards;
        }
        .fade-in-up {
          opacity: 0;
          transform: translateY(40px);
          animation: fadeInUp 1.2s cubic-bezier(0.4, 0, 0.2, 1) 0.3s forwards;
        }
        @keyframes fadeIn {
          to {
            opacity: 1;
          }
        }
        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: none;
          }
        }
        /* --- ENHANCED GLASS CARDS --- */
        .glass-card,
        .zentora-about-left,
        .zentora-feature-card,
        .zentora-audience-card,
        .zentora-contact-card {
          background: rgba(255, 255, 255, 0.6);
          box-shadow: 0 2px 16px rgba(79, 140, 255, 0.06);
          border-radius: 32px;
          border: none;
          backdrop-filter: blur(10px);
        }
        .zentora-feature-card,
        .zentora-about-left,
        .zentora-audience-card,
        .zentora-contact-card {
          box-shadow: 0 2px 16px rgba(79, 140, 255, 0.06);
        }
        .zentora-feature-card:hover,
        .zentora-about-left:hover,
        .zentora-audience-card:hover,
        .zentora-contact-card:hover {
          box-shadow: 0 8px 32px rgba(162, 89, 255, 0.1);
          transform: translateY(-2px) scale(1.02);
        }
        .zentora-section-title {
          font-family: "PP Editorial", serif;
          font-size: clamp(2.2rem, 5vw, 3.2rem);
          font-weight: 400;
          color: #23273a;
          margin-bottom: 18px;
          letter-spacing: -0.03em;
          text-align: center;
        }
        .zentora-section-title .italic {
          font-family: "PP Editorial Italic", serif;
          font-style: italic;
        }
        .gradient-text {
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .zentora-audience {
          background: none;
          padding: 120px 20px 100px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .zentora-audience-cards {
          display: flex;
          justify-content: center;
          width: 100%;
        }
        .zentora-audience-card {
          margin-top: 32px;
          min-width: 340px;
          max-width: 600px;
          width: 100%;
        }
        .zentora-audience-tabs.centered-tabs {
          display: flex;
          justify-content: center;
          gap: 12px;
          margin-bottom: 18px;
        }
        .zentora-audience-tabs button {
          background: #f3f3f7;
          border: none;
          border-radius: 18px;
          padding: 8px 22px;
          font-size: 1.05rem;
          color: #23273a;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s, color 0.2s;
        }
        .zentora-audience-tabs button.active,
        .zentora-audience-tabs button:hover {
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
        }
        .zentora-academic {
          padding: 100px 20px 100px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: none;
        }
        .zentora-academic-grid {
          display: flex;
          flex-direction: row;
          align-items: stretch;
          justify-content: center;
          gap: 72px;
          max-width: 1200px;
          width: 100%;
        }
        .zentora-academic-visual {
          min-width: 220px;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          margin-top: auto;
          margin-bottom: auto;
        }
        .zentora-academic-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        .zentora-academic-sub {
          color: #7a7e8c;
          font-size: 1.13rem;
          margin-bottom: 32px;
        }
        .zentora-academic-features {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 28px;
          width: 100%;
        }
        .zentora-academic-feature {
          background: rgba(255, 255, 255, 0.6);
          box-shadow: 0 2px 16px rgba(79, 140, 255, 0.06);
          border-radius: 32px;
          padding: 28px 24px 22px;
          min-height: 120px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          backdrop-filter: blur(8px);
          transition: all 0.3s ease;
        }
        .zentora-academic-feature:hover {
          box-shadow: 0 8px 32px rgba(162, 89, 255, 0.1);
          transform: translateY(-2px) scale(1.02);
        }
        .zentora-academic-feature-title {
          font-size: 1.13rem;
          font-weight: 700;
          color: #23273a;
          margin-bottom: 8px;
        }
        .zentora-academic-feature-desc {
          color: #7a7e8c;
          font-size: 1.01rem;
        }
        .zentora-footer {
          background: #fafaf7;
          padding: 40px 20px;
          border-top: 1px solid rgba(0, 0, 0, 0.05);
        }
        .zentora-footer-content {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .zentora-footer-copyright,
        .zentora-footer-tagline {
          color: #8a8e9c;
          font-size: 0.85rem;
          margin: 0;
        }
        .zentora-footer-tagline {
          font-style: italic;
          opacity: 0.8;
        }
        @media (max-width: 900px) {
          .zentora-features-grid,
          .zentora-contact-cards {
            grid-template-columns: 1fr 1fr;
            gap: 18px;
          }
          .zentora-about-content.glass-card {
            padding: 24px 10px 18px 10px;
          }
          .zentora-hero-content {
            padding-top: 80px;
          }
          .zentora-academic-grid {
            flex-direction: column;
            gap: 32px;
          }
          .zentora-academic-features {
            grid-template-columns: 1fr;
          }
          .zentora-nav-buttons {
            flex-wrap: wrap;
            gap: 2px;
            padding: 6px;
          }
          .zentora-nav-btn {
            padding: 8px 12px;
            font-size: 12px;
          }
        }
        @media (max-width: 600px) {
          .zentora-nav-inner {
            flex-direction: column;
            gap: 12px;
            padding: 10px 10px;
          }
          .zentora-features-grid,
          .zentora-contact-cards {
            grid-template-columns: 1fr;
          }
          .zentora-audience-card {
            padding: 24px 10px 18px 10px;
          }
          .zentora-contact-card {
            min-width: 200px;
            max-width: 320px;
            padding: 24px 20px;
            margin: 0 auto;
          }
          .zentora-hero-content {
            padding-top: 80px;
          }
          .zentora-nav-buttons {
            flex-direction: column;
            gap: 4px;
          }
          .zentora-nav-btn {
            padding: 10px 16px;
            text-align: center;
          }
          .zentora-footer-content {
            flex-direction: column;
            gap: 20px;
            text-align: center;
          }
        }
        /* --- ABOUT SECTION (LESS BOXY) --- */
        .zentora-about {
          padding: 120px 20px 100px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .zentora-about-grid {
          display: flex;
          flex-direction: row;
          align-items: stretch;
          justify-content: center;
          gap: 64px;
          max-width: 1200px;
          width: 100%;
          min-height: 520px;
        }
        .zentora-about-left {
          flex: 1.1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          min-width: 340px;
          max-width: 520px;
          width: 100%;
          padding: 48px 36px 48px 36px;
          min-height: 420px;
          height: auto;
          padding-bottom: 64px;
          background: rgba(255, 255, 255, 0.75);
          box-shadow: 0 8px 48px 0 rgba(79, 140, 255, 0.1), 0 1.5px 6px 0 rgba(162, 89, 255, 0.08);
          border-radius: 36px;
          border: none;
          backdrop-filter: blur(16px);
        }
        .zentora-about-features {
          flex: 1.1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          width: 100%;
          height: 100%;
          margin-top: 0;
        }
        .zentora-features-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: 1fr 1fr;
          gap: 28px;
          width: 100%;
          max-width: 520px;
          align-self: center;
          margin-top: 100px;
        }
        .zentora-feature-card {
          background: rgba(255, 255, 255, 0.55);
          box-shadow: 0 8px 32px rgba(79, 140, 255, 0.1);
          border-radius: 32px;
          padding: 32px 22px 24px 22px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          min-width: 0;
          min-height: 0;
          width: 100%;
          height: 100%;
          box-sizing: border-box;
          border: none;
          transition: box-shadow 0.2s, transform 0.2s;
          backdrop-filter: blur(12px);
        }
        .zentora-feature-card:hover {
          box-shadow: 0 16px 48px 0 rgba(162, 89, 255, 0.13), 0 1.5px 6px 0 rgba(79, 140, 255, 0.08);
          transform: translateY(-4px) scale(1.04);
        }
        .zentora-feature-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #23273a;
          margin-bottom: 10px;
        }
        .zentora-feature-desc {
          color: #7a7e8c;
          font-size: 1.08rem;
        }
        /* --- END ABOUT SECTION --- */
        @media (max-width: 900px) {
          .zentora-about-grid {
            flex-direction: column;
            gap: 32px;
            min-height: unset;
          }
          .zentora-about-left {
            padding: 28px 10px 28px 10px;
            max-width: 100%;
            height: unset;
          }
          .zentora-about-features {
            max-width: 100%;
            height: unset;
          }
          .zentora-features-grid {
            grid-template-columns: 1fr 1fr;
            max-width: 100%;
            height: unset;
          }
        }
        @media (max-width: 600px) {
          .zentora-features-grid {
            grid-template-columns: 1fr;
            grid-template-rows: unset;
            height: unset;
          }
        }
        .typewriter-cursor {
          display: inline-block;
          width: 1ch;
          color: #a259ff;
          animation: blink 1.1s steps(1) infinite;
        }
        @keyframes blink {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0;
          }
        }
        /* Glassmorphism Modal */
        .glass-modal.password-modal {
          background: rgba(255, 255, 255, 0.82);
          border-radius: 28px;
          border: 1.5px solid rgba(120, 100, 255, 0.13);
          box-shadow: 0 8px 40px #a259ff18, 0 0 0 2px #4f8cff11;
          backdrop-filter: blur(18px) saturate(1.2);
          color: #23273a;
          position: relative;
          overflow: hidden;
          padding-top: 48px;
        }
        .zentora-modal-logo {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 12px;
          animation: logoFloat 3.2s ease-in-out infinite alternate;
        }
        .zentora-modal-logo img {
          width: 64px;
          height: 64px;
          object-fit: contain;
          filter: drop-shadow(0 2px 16px #a259ff33);
        }
        @keyframes logoFloat {
          0% {
            transform: translateY(0);
          }
          100% {
            transform: translateY(-10px);
          }
        }
        .modal-title-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 0px;
        }
        .modal-lock-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 2px;
        }
        .modal-title {
          font-family: "PP Editorial Italic", serif;
          font-size: 2.4rem;
          font-weight: 400;
          color: #23273a;
          margin-bottom: 0px;
          letter-spacing: -0.01em;
        }
        .modal-subtitle {
          font-family: "FK Grotesk", sans-serif;
          color: #8a8e9c;
          font-size: 1.25rem;
          margin-bottom: 32px;
          text-align: center;
        }
        .modal-input {
          width: 100%;
          max-width: 340px;
          padding: 18px;
          background: rgba(240, 240, 255, 0.55);
          border: 1.5px solid #e0e0ff;
          border-radius: 14px;
          color: #23273a;
          font-size: 1.35rem;
          font-family: "FK Grotesk", sans-serif;
          margin-bottom: 24px;
          text-align: center;
          transition: border 0.3s, box-shadow 0.3s;
          outline: none;
          box-shadow: 0 0 0 0 #a259ff00;
        }
        .modal-input:focus {
          border: 1.5px solid #a259ff;
          box-shadow: 0 0 16px 0 #a259ff44, 0 0 32px 0 #4f8cff22;
          background: rgba(255, 255, 255, 0.85);
        }
        .modal-input.error {
          border: 1.5px solid #ff3b30;
        }
        .modal-submit-btn {
          width: 100%;
          max-width: 340px;
          padding: 18px;
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          border: none;
          border-radius: 14px;
          font-family: "FK Grotesk", sans-serif;
          font-weight: 600;
          font-size: 1.25rem;
          cursor: pointer;
          margin-top: 8px;
          margin-bottom: 0;
          transition: box-shadow 0.3s, background 0.3s;
          box-shadow: 0 4px 24px #a259ff22;
        }
        .modal-submit-btn:hover {
          background: linear-gradient(90deg, #a259ff 0%, #4f8cff 100%);
          box-shadow: 0 8px 32px #4f8cff33;
        }
        .error-message {
          color: #ff3b30;
          font-size: 0.95rem;
          display: block;
          margin-top: -15px;
          margin-bottom: 15px;
          text-align: center;
        }
        /* Matrix/Secret Code Effect */
        .matrix-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          overflow: hidden;
        }
        .matrix-rain {
          position: absolute;
          width: 100%;
          height: 100%;
          opacity: 0.13;
          background: repeating-linear-gradient(120deg, #a259ff 0 2px, transparent 2px 16px);
          animation: matrixMove 3.5s linear infinite;
        }
        @keyframes matrixMove {
          0% {
            transform: translateY(-16px);
          }
          100% {
            transform: translateY(16px);
          }
        }
        .password-modal form {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
        }
        .modal-close {
          position: absolute;
          top: 20px;
          right: 20px;
          background: none;
          border: none;
          color: #666;
          cursor: pointer;
          padding: 5px;
          transition: all 0.3s ease;
          z-index: 2;
        }
        .modal-close:hover {
          color: #23273a;
        }
        .platform-option-icon svg {
          display: block;
          margin: 0 auto;
        }
        .student-chat-instruction {
          color: #8a8e9c;
          text-align: center;
          font-size: 1.08rem;
          margin-bottom: 18px;
        }
        .student-subjects-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px 18px;
          margin-bottom: 8px;
        }
        .student-subject-btn {
          min-width: 0;
          width: 100%;
          max-width: 240px;
          padding: 14px 12px;
          font-size: 1.01rem;
          border-radius: 14px;
          margin: 0 auto;
        }
        @media (max-width: 700px) {
          .student-subjects-grid {
            grid-template-columns: 1fr;
          }
          .student-subject-btn {
            max-width: 100%;
          }
        }
        .platform-option-icon svg {
          display: block;
          margin: 0 auto;
        }
        .dashboard-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(36, 34, 54, 0.32);
          z-index: 3000;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: fadeIn 0.3s;
        }
        .dashboard-modal {
          background: #fff;
          border-radius: 22px;
          box-shadow: 0 8px 40px #a259ff22, 0 0 0 2px #4f8cff11;
          padding: 44px 38px 32px 38px;
          min-width: 320px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          font-family: "FK Grotesk", sans-serif;
          animation: scaleIn 0.3s;
        }
        .dashboard-modal-title {
          font-family: "PP Editorial", serif;
          font-size: 2rem;
          color: #23273a;
          margin-bottom: 10px;
        }
        .dashboard-modal-btn {
          background: linear-gradient(90deg, #4f8cff 0%, #a259ff 100%);
          color: #fff;
          border: none;
          border-radius: 12px;
          font-size: 1.1rem;
          font-weight: 600;
          padding: 16px 38px;
          margin: 8px 0;
          cursor: pointer;
          box-shadow: 0 4px 18px #a259ff22;
          transition: background 0.2s, transform 0.2s;
        }
        .dashboard-modal-btn:hover {
          background: linear-gradient(90deg, #a259ff 0%, #4f8cff 100%);
          transform: translateY(-2px) scale(1.04);
        }
        .dashboard-modal-hint {
          color: #8a8e9c;
          font-size: 0.98rem;
          margin-top: 10px;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.92);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
      <Analytics />
    </div>
  );
};

// Replace the static SVG in the Academic Intelligence section with an animated React component
// Animated Academic Intelligence Visual
const AnimatedMatchingSVG = () => (
  <div className="zentora-animated-matching">
    <svg width="220" height="400" viewBox="0 0 220 400" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Lines (animated draw) */}
      <line className="match-line" x1="50" y1="88" x2="50" y2="112" stroke="#a259ff" strokeWidth="2" opacity="0.18" />
      <line className="match-line" x1="50" y1="168" x2="50" y2="192" stroke="#a259ff" strokeWidth="2" opacity="0.18" />
      <line className="match-line" x1="50" y1="248" x2="50" y2="272" stroke="#a259ff" strokeWidth="2" opacity="0.18" />
      <line className="match-line" x1="78" y1="60" x2="170" y2="180" stroke="#4f8cff" strokeWidth="2" opacity="0.13" />
      <line className="match-line" x1="78" y1="140" x2="170" y2="180" stroke="#4f8cff" strokeWidth="2" opacity="0.13" />
      <line className="match-line" x1="78" y1="220" x2="170" y2="180" stroke="#a259ff" strokeWidth="2" opacity="0.13" />
      <line className="match-line" x1="78" y1="300" x2="170" y2="180" stroke="#a259ff" strokeWidth="2" opacity="0.13" />
      {/* Nodes (animated pulse/float) */}
      <circle className="match-node node1" cx="50" cy="60" r="28" fill="#4f8cff" fillOpacity="0.18" />
      <circle className="match-node node2" cx="50" cy="140" r="28" fill="#4f8cff" fillOpacity="0.18" />
      <circle className="match-node node3" cx="50" cy="220" r="28" fill="#4f8cff" fillOpacity="0.18" />
      <circle className="match-node node4" cx="50" cy="300" r="28" fill="#a259ff" fillOpacity="0.18" />
      {/* Central glowing node */}
      <circle className="match-center-node" cx="170" cy="180" r="38" fill="url(#orb)" />
      <defs>
        <radialGradient id="orb" cx="0.5" cy="0.5" r="0.5" gradientTransform="matrix(1 0 0 1 0 0)">
          <stop stopColor="#a259ff" stopOpacity="0.7" />
          <stop offset="1" stopColor="#4f8cff" stopOpacity="0.2" />
        </radialGradient>
      </defs>
    </svg>
    <style>{`
      .zentora-animated-matching {
        width: 220px;
        height: 320px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .match-node {
        transform-box: fill-box;
        transform-origin: 50% 50%;
        animation: nodePulse 2.8s ease-in-out infinite;
      }
      .node1 { animation-delay: 0s; }
      .node2 { animation-delay: 0.5s; }
      .node3 { animation-delay: 1s; }
      .node4 { animation-delay: 1.5s; }
      @keyframes nodePulse {
        0%, 100% { transform: scale(1) translateY(0); }
        30% { transform: scale(1.08) translateY(-8px); }
        60% { transform: scale(0.96) translateY(6px); }
      }
      .match-center-node {
        filter: blur(0.5px) drop-shadow(0 0 32px #a259ff66);
        animation: centerGlow 2.2s ease-in-out infinite alternate;
        transform-box: fill-box;
        transform-origin: 50% 50%;
      }
      @keyframes centerGlow {
        0% { filter: blur(0.5px) drop-shadow(0 0 32px #a259ff66); }
        100% { filter: blur(2.5px) drop-shadow(0 0 48px #a259ff99); }
      }
      .match-line {
        stroke-dasharray: 200;
        stroke-dashoffset: 200;
        animation: lineDraw 1.2s cubic-bezier(0.4,0,0.2,1) forwards;
      }
      .match-line:nth-child(1) { animation-delay: 0.2s; }
      .match-line:nth-child(2) { animation-delay: 0.3s; }
      .match-line:nth-child(3) { animation-delay: 0.4s; }
      .match-line:nth-child(4) { animation-delay: 0.6s; }
      .match-line:nth-child(5) { animation-delay: 0.8s; }
      .match-line:nth-child(6) { animation-delay: 1s; }
      .match-line:nth-child(7) { animation-delay: 1.2s; }
      @keyframes lineDraw {
        to { stroke-dashoffset: 0; }
      }
    `}</style>
  </div>
);

// Iridescent SVG lines for editorial effect
const IridescentLines = () => (
  <>
    {/* Top arc */}
    <svg
      className="iridescent-line top"
      width="100%"
      height="90"
      viewBox="0 0 1440 90"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="iridescent1" x1="0" y1="0" x2="1440" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#a259ff" />
          <stop offset="25%" stopColor="#4f8cff" />
          <stop offset="50%" stopColor="#43e6b0" />
          <stop offset="75%" stopColor="#ffe066" />
          <stop offset="100%" stopColor="#ff6bcb" />
        </linearGradient>
      </defs>
      <path d="M0,80 Q720,0 1440,80" stroke="url(#iridescent1)" strokeWidth="2.5" fill="none" opacity="0.13" />
    </svg>
    {/* Middle arc */}
    <svg
      className="iridescent-line mid"
      width="100%"
      height="120"
      viewBox="0 0 1440 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="iridescent2" x1="0" y1="0" x2="1440" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#ff6bcb" />
          <stop offset="30%" stopColor="#a259ff" />
          <stop offset="60%" stopColor="#4f8cff" />
          <stop offset="100%" stopColor="#43e6b0" />
        </linearGradient>
      </defs>
      <path d="M0,100 Q720,20 1440,100" stroke="url(#iridescent2)" strokeWidth="2" fill="none" opacity="0.10" />
    </svg>
    {/* Bottom arc */}
    <svg
      className="iridescent-line bottom"
      width="100%"
      height="100"
      viewBox="0 0 1440 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="iridescent3" x1="0" y1="0" x2="1440" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#ffe066" />
          <stop offset="40%" stopColor="#43e6b0" />
          <stop offset="80%" stopColor="#4f8cff" />
          <stop offset="100%" stopColor="#a259ff" />
        </linearGradient>
      </defs>
      <path d="M0,90 Q720,10 1440,90" stroke="url(#iridescent3)" strokeWidth="2.5" fill="none" opacity="0.10" />
    </svg>
    <style>{`
      .iridescent-line {
        position: absolute;
        left: 0;
        width: 100vw;
        pointer-events: none;
        z-index: 0;
        user-select: none;
      }
      .iridescent-line.top { top: 0; }
      .iridescent-line.mid { top: 38vh; }
      .iridescent-line.bottom { bottom: 0; }
      @media (max-width: 900px) {
        .iridescent-line.mid { top: 48vh; }
      }
      @media (max-width: 600px) {
        .iridescent-line { display: none; }
      }
    `}</style>
  </>
);

export default ZentoraWebsite;
